import { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, lazy, Suspense } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Slide from "@mui/material/Slide";
import Fade from "@mui/material/Fade";
import { bindData } from "@aelive/data-binding-library";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { useMutation, gql } from "@apollo/client";
import { sendPatchesFromDrivePreset, sendLiveEventPreset, removeBaseDataPreset, resetFixturePreset } from "@aelive/dds-client";
import { useEventService } from "../../hooks/useEventService";
import { usePresetShortcuts, ShortcutRegistry } from "@aelive/shortcut-service";
import { useFixtureData } from "../../hooks/useFixtureData";
import { useBrokerService } from "../../hooks/useBrokerService";
import { useClockManager, getPeriodEndSeconds, isMatchEnded } from "../../hooks/useClockManager";
import { useServerClock } from "../../hooks/useServerClock";
import { useBrokerSubscription } from "../../hooks/useBrokerSubscription";
import { getContrastYIQ } from "../../utils/colors";
import {
  buildLineup,
  actionToSection,
  formatDdsEventForTimeline,
  createPlayerLookup,
  pickStickyLineup,
  computeRedCardTabs,
  computeOpusGoalScores,
  buildScorersByTeam,
  findLatestTeamGoal,
} from "../../utils/txLiveHelpers";
import { getEventName, getTeamFirstName, playerKeys } from "../../utils/playerName";
import { subGroupForEdit } from "../../utils/substitutionUtils";
import { buildTeamStats } from "../../utils/teamStats";
import { getCachedStatsConfig } from "../../services/statsConfig.service";
import { useMatchStatsTiles } from "../../hooks/useMatchStatsTiles";
import { useOnAirStatsCaption } from "../../hooks/useOnAirStatsCaption";
import HeadlessStatsAnimator from "../../components/copilot/HeadlessStatsAnimator";
import apiService from "../../services/api.service";
import { getStoredSession } from "../../utils/sessionManager";
import { isCopilotView } from "../../utils/viewMode";
import stripScoreFromOverlayTake from "../../utils/overlayScoreGuard";
import { deriveRendererLock } from "../../utils/rendererLock";
import { pushNotification } from "../../utils/notificationLog";
import { resolveTeamLogo } from "../../utils/teamBadge";
import { dds } from "../../dds/ddsClient";
import { withOptaEvents } from "../../dds/optaEvents";
import { useOptaEvents } from "../../hooks/useOptaEvents";
import LineupPanel from "../../components/organisms/LineupPanel/LineupPanel";
import StatsPanels from "../../components/organisms/StatsPanel/StatsPanel";
import ClockTimeInputDialog from "../../components/organisms/StatsPanel/ClockTimeInputDialog";
import ResetClockConfirmDialog from "../../components/organisms/StatsPanel/ResetClockConfirmDialog";
import OverrideClockConfirmDialog from "../../components/organisms/StatsPanel/OverrideClockConfirmDialog";
// findSquadDuplicates is a pure helper — imported from the util so the
// on-load scan doesn't await the (lazy) dialog chunk.
import { findSquadDuplicates } from "../../utils/duplicateDetection";
import { Loader, ClearButton } from "../../components/atoms";
import { CaptionSelector, CaptionButton } from "@ae/ui";
import { DriveMaestro } from "@ae/maestrov2-form-lib";
import CopilotMaestroChrome from "./copilot/CopilotMaestroChrome";
import { useDeveloperPanel } from "./copilot/useDeveloperPanel";
import {
  SubstitutionPanel,
  AdditionalTimePanel,
  ClockControl,
  CopilotClockResetWidget,
  CopilotClockOverwriteWidget,
  CopilotMatchStatsWidget,
  CopilotSubstitutionWidget,
  TabControl,
  VERSION as MAESTRO_UI_VERSION,
} from "@ae/maestro-ui-lib";
import { buildCopilotCaptionWidgets } from "../../config/copilotCaptionWidgets";
import { useCopilotTabControl } from "./useCopilotTabControl";
import { useCopilotSpatialNav } from "./useCopilotSpatialNav";
import { fitCanvas } from "./copilot/copilotCanvas";
import { statTileTitle, statTileSubtitle } from "./copilot/statTileTitle";
import { statOnAirChange } from "./copilot/statOnAirChange";
import { useCopilotButtonShortcuts } from "./useCopilotButtonShortcuts";
import ManchsterLogo from "../../assets/images/ManchesterLogo.png";
import GrimsbyTownFCLogo from "../../assets/images/GrimsbyTownFCLogo.png";
import { getClockProfile } from "../../config/clockProfiles";
import { resolveProfileId } from "../../utils/profile";
import { getGraphicForCaption } from "../../config/captionGraphicMap";
import { COPILOT_LAYOUT_PRESETS } from "../../config/copilotLayouts";
import { writeProgramRendererUrl } from "../../utils/maestroSessionSettings";
import { Responsive } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// NOTE: we deliberately do NOT use react-grid-layout's WidthProvider here.
// In this build its `measureBeforeMount` only renders an empty div and then
// flips a `mounted` flag without measuring — the grid first lays out at the
// hardcoded 1280px width and the ResizeObserver later jumps it to the real
// width, which is the left-to-right "slide" on load. Instead we measure the
// container ourselves via a ref callback (fires during commit, before paint)
// and feed the width straight to <Responsive>, so the first paint is final.

// Lazy-loaded dialogs (P10). Operator-triggered modals — deferring them
// trims the initial fixture-load bundle. The dialog cluster is wrapped
// in <Suspense fallback={null}> further down — flash of empty space is
// fine because each is operator-triggered.
const CopilotSubstitutionDialog = lazy(() => import("../../components/organisms/StatsPanel/CopilotSubstitutionDialog"));
const AddGoalDetailsDialog = lazy(() => import("../../components/organisms/StatsPanel/AddGoalDetailsDialog"));
const DuplicateInfoDialog = lazy(() => import("../../components/organisms/LineupPanel/DuplicateInfoDialog"));
// OPUS-1505 — diagnostic only, and super-admin only, so it must never be in
// the bundle an ordinary operator downloads on a match day.
const DdsObjectViewer = lazy(() => import("../../components/organisms/DdsObjectViewer/DdsObjectViewer"));

// Stable identity for DriveMaestro's additionalFields prop (P1). Inline {}
// on every render busts the form-lib's memoization and forces caption-form
// remounts. The card-animate feature that previously populated this object
// now flows through LineupPanel's local state — TXLive's mount passes
// `{}` permanently for now.
const MAESTRO_ADDITIONAL_FIELDS = Object.freeze({});

// Default operator UI settings for the Maestro caption modal — clean
// gameday view: data-source fields hidden, preview window on, all
// other panels/toggles off. Operators' own cog toggles persist over
// these (defaultUiSettings only seeds fresh state).
const MAESTRO_DEFAULT_UI_SETTINGS = Object.freeze({
  hideDataSourceFields: true,
  hideCaptionDrivenFields: false,
  showJsonViewer: false,
  ignoreHides: false,
  showForm: false,
  showPreview: true,
});

const SEND_PATCHES = gql`
  mutation SendPatches($patches: [JSON!]!) {
    sendPatches(patches: $patches)
  }
`;

// Graphic that hosts the per-stat MatchStats captions (the score graphic — its
// breadcrumb reads "PermScore / MatchStats").
const STAT_GRAPHIC = "gfx_PermScore";
// Copilot spacing on the 1920x1080 canvas: the gap from the screen edge
// (and the footer) to the panels, and the gap between panels.
const COPILOT_EDGE = 24;
const COPILOT_GAP = 16;

// Recognized "match finished" / "match in progress" / "half time"
// fixture_status values (see the one-time period-seeding effect in TXLive
// below). "In progress" statuses are ambiguous on their own — combined with
// fixture_session (1/2) they resolve to First Half / Second Half.
const FINISHED_STATUSES = new Set(["finished", "full time", "ft"]);
const IN_PROGRESS_STATUSES = new Set(["latest"]);
const HALF_TIME_STATUSES = new Set(["halftime", "half time", "ht"]);

// Parse the match clock ("MM:SS" or a number of seconds) to whole seconds.
// Used to stamp a goal at the exact second the '+' button was hit.
const parseClockToSeconds = (t) => {
  if (typeof t === "number") return Math.max(0, Math.round(t));
  if (typeof t === "string") {
    const [m, s] = t.split(":");
    return (parseInt(m, 10) || 0) * 60 + (parseInt(s, 10) || 0);
  }
  return 0;
};

// Maps a copilot stat-button id (see teamStats STAT_CONFIG) to the matching
// gfx_PermScore "MatchStats_<Stat>" sub-caption. Opening that sub-caption is what
// auto-selects the stat in the MatchStats caption's nav dropdown.
const STAT_CAPTION = {
  possessionPercentage: "MatchStats_Possession",
  possession_1: "MatchStats_Possession",
  possession_2: "MatchStats_2ndHalfPossession",
  totalScoringAtt: "MatchStats_Attempts",
  ontargetScoringAtt: "MatchStats_ShotsOnTarget",
  fkFoulLost: "MatchStats_Fouls",
  wonCorners: "MatchStats_Corners",
  totalOffside: "MatchStats_Offsides",
  totalTackle: "MatchStats_Tackles",
  wonTackle: "MatchStats_Tackles",
  accuratePass: "MatchStats_CompletedPasses",
  passAccuracy: "MatchStats_PassAccuracy",
  totalYellowCard: "MatchStats_YellowCards",
  totalRedCard: "MatchStats_RedCards",
};


const TXLive = ({
  orgId,
  fixtureId,
  rendererId,
  fixtureOverlayOpen,
  fixtureOverlayContent,
  onCloseFixtureOverlay,
  rendererOverlayOpen,
  rendererOverlayContent,
  isLive = false,
  isNleEnabled = false,
  onCloseRendererOverlay,
  onOpenRendererOverlay,
  showChangeRendererButton = false,
  hasFixtureSelected = false,
  hasRendererSelected = false,
  readOnly = false,
  lockInfo = null,
  onLineupErrorsChange,
  onBrokerStatusChange,
  // OPUS-1505 — already gated to super admins in AppRoutes; TXLive is simply
  // the only place the DDS snapshot is in scope, so it is where the viewer
  // mounts. Lifting fixtureState up to AppRoutes instead would re-render the
  // whole shell on every DDS patch — several times a second during a match.
  ddsViewerOpen = false,
  onCloseDdsViewer,
  // The selected competition's profile, passed down rather than read from
  // localStorage — that key was shared by every tab on the origin, so two
  // competitions open side by side fought over one value. Undefined on the
  // shared-session route, which has no picker; resolveProfileId falls through
  // to the fixture's own profile there.
  selectedProfileId,
}) => {
  const competitionId = localStorage.getItem("selectedCompetitionId");
  const seasonId = localStorage.getItem("selectedSeasonId");
  const selectedFixtureId =
    fixtureId || localStorage.getItem("selectedFixtureId");

  const [sendPatchMutation] = useMutation(SEND_PATCHES);

  // Ref-based delegation so useFixtureData can call back into this component
  // without needing showNotification to be in scope at hook-call time
  // (showNotification is declared further down).
  const lineupChangeHandlerRef = useRef(null);
  const handleLineupChange = useCallback((info) => {
    lineupChangeHandlerRef.current?.(info);
  }, []);

  const {
    loading,
    fixtureState,
    fixture,
    result,
    homeTeam,
    awayTeam,
    events: fixtureEvents,
    updateFixtureState,
  } = useFixtureData(competitionId, seasonId, selectedFixtureId, { onLineupChange: handleLineupChange });
  // Single source of profile for this page — every panel, dialog, event write
  // and the clock config reads this one value. See resolveProfileId for the
  // precedence (fixture -> stored -> default).
  const profileId = useMemo(
    () => resolveProfileId(fixtureState?.competition?.[0]?.profile_id, selectedProfileId),
    [fixtureState, selectedProfileId],
  );
  // The fixture's display name, sent with every event. Only the season's
  // fixture rows carry it; the root fixture query does not ask for it.
  const fixtureName = useMemo(
    () => (fixtureState?.season?.[0]?.fixture || []).find((f) => f?.fixture_id === selectedFixtureId)?.fixture_name ?? null,
    [fixtureState, selectedFixtureId],
  );
  const [period, setPeriod] = useState("PRE-GAME");
  const [selectedRenderer, setSelectedRenderer] = useState(null);

  // Renderer-lock verdict — single source of truth for "view-only" mode.
  // When locked, TXLive still renders (so the operator can see the live match)
  // but every mutating handler short-circuits (below) and the broker choke-point
  // (useBrokerService.sendMutation) suppresses all graphic commands. Computed
  // here, above useBrokerService, so the same flag feeds the broker guard, the
  // handler guards, and the header lock badge. `readOnly` also covers the
  // SharedSessionPage shared-view case (no lockInfo → no "Locked by" name).
  const { isRendererLocked, lockedByName } = deriveRendererLock({
    readOnly,
    lockInfo,
    selectedRenderer,
    // Pass the session so the raw owner_lock fallback honours the lock-owner /
    // super-admin exemption (they get full access to a renderer they locked).
    session: getStoredSession(),
  });

  const { actionState, dataState } = useBrokerSubscription(
    selectedRenderer?.renderer_id || rendererId,
  );
  // OPUS-1172: the match clock no longer reads broker state (dataState /
  // VizGlobal.Clocks). It's derived from the fixture's clock_* events and ticks
  // against DDS server time — see useClockManager below.

  const userRole = getStoredSession()?.role;
  // `userRole` stays the real role (used for event telemetry below). The
  // rendered view, however, honours a privileged user's view override
  // (utils/viewMode) — so admins/testers can preview the copilot view.
  const isCopilot = isCopilotView(getStoredSession());

  // Live recording context for the generic Maestro-action recorder
  // (handleMaestroAction, wired to Maestro's onUserAction prop). Declared here but
  // populated every render below once clock/fixture/period are resolved; read
  // lazily when an action fires so it always holds the latest values.
  const matchContextRef = useRef({});

  // Warn the user once per burst when the broker choke-point (useBrokerService.
  // sendMutation) suppresses a graphics command because the renderer is locked.
  // Throttled so one user action that fans out into several broker calls (e.g. a
  // BAKE + ANIMATION pair) reports one refusal, not several. Kept even though the
  // notification log collapses repeats: suppressing the message is cheaper than
  // writing it and collapsing it, and "locked ×2" would read as two separate
  // refusals rather than the one that actually happened.
  const lastLockedToastRef = useRef(0);
  const notifyLockedCommandSuppressed = useCallback((byName) => {
    const now = Date.now();
    if (now - lastLockedToastRef.current < 3000) return;
    lastLockedToastRef.current = now;
    pushNotification(
      byName ? `Renderer is locked by ${byName} — animate command cannot be sent.` : 'Renderer is locked — animate command cannot be sent.',
      'warning',
    );
  }, []);

  const {
    updateRenderer,
    triggerScoreUpdate,
    getRendererIdToUse,
    sendMutation: brokerSendMutation,
    sendApplyDataState,
    clearOutput,
    cancelPendingScoreFinalise,
  } = useBrokerService(rendererId, selectedRenderer, {
    isRendererLocked,
    lockedByName,
    onLockedCommandSuppressed: notifyLockedCommandSuppressed,
  });

  // Maestro calls this after every committed operator action. We forward the
  // raw caption command + match context to the event service, which decides
  // (via its caption-action registry) whether to turn it into an event — today
  // only ADDITIONAL_TIME_IN. No allow-list lives here. The event service entry
  // is read off the context ref since eventService is created further below.
  const noteCaptionUseRef = useRef(null);
  const noteStatActionRef = useRef(null);
  const handleMaestroAction = useCallback((cmd) => {
    // OPUS-1619 — the command names the graphic and the caption the operator
    // is working in (Maestro builds it as { layer, preset, caption }), so the
    // NLE block is read from that exact caption rather than from a map.
    noteCaptionUseRef.current?.(cmd);
    noteStatActionRef.current?.(cmd);

    const ctx = matchContextRef.current;
    if (typeof ctx?.handleCaptionAction !== "function") return;
    const [m, s] = String(ctx.clockTime || "0:00").split(":").map(Number);
    const timeInSeconds = (Number.isNaN(m) ? 0 : m) * 60 + (Number.isNaN(s) ? 0 : s);
    // Additional time is a single authoritative row per period — find the
    // existing entry (if any) so a re-announced value replaces it instead of
    // appending a duplicate timeline entry. The event service deletes this
    // id and writes a fresh one (see additional_time_event_object_builder.js)
    // rather than updating in place, so the row's timeline position advances
    // too.
    const existingAdditionalTime = (ctx.fixtureEvents || [])
      .filter((e) => (e.event_key || e.event_type) === "additional_time"
        && String(e.game_period) === String(ctx.session))
      .sort((a, b) => (b.time_in_seconds ?? 0) - (a.time_in_seconds ?? 0))[0];
    Promise.resolve(
      ctx.handleCaptionAction(cmd?.caption, {
        fixtureId: ctx.fixtureId,
        competitionId: ctx.competitionId,
        seasonId: ctx.seasonId,
        profile_id: ctx.profileId,
        user_role: ctx.userRole,
        game_period: ctx.session,
        time_in_seconds: timeInSeconds,
        existing_event_id: existingAdditionalTime?.event_id,
      }),
    ).catch((e) => console.error("Failed to record Maestro action event", e));
  }, []);

  const clockProfile = getClockProfile(profileId);

  // OPUS-1172: server-anchored now (polls DDS serverTime) so the clock never
  // depends on the operator's system clock.
  const getServerNow = useServerClock();

  const {
    clockTime,
    currentClockDirection,
    clockStatus,
    handleClockAction: apiClockAction,
  } = useClockManager(fixtureEvents, getServerNow, brokerSendMutation, {
    firstHalfLimit: clockProfile.firstHalfLimit,
    secondHalfLimit: clockProfile.secondHalfLimit,
    firstHalfEndSeconds: clockProfile.firstHalfEndSeconds,
    secondHalfEndSeconds: clockProfile.secondHalfEndSeconds,
  });

  // --- Stats / Events timeline (real data from DDS subscription) ---
  const [statsViewMode, setStatsViewMode] = useState("stats"); // 'stats' | 'events'

  // player_id → player Map, rebuilt ONLY when the teams change — not on every
  // event tick. formatDdsEventForTimeline consumes it O(1) per event (it used to
  // .find() across [home + away] for every event → O(n²) on 50+ events).
  const playerLookup = useMemo(
    () => createPlayerLookup(homeTeam, awayTeam),
    [homeTeam, awayTeam],
  );

  const eventLog = useMemo(() => {
    const getSortVal = (e) => {
      const u = e.updatedAt ?? e.updated_at;
      if (u) return new Date(u).getTime();
      return (Number(e.time_in_seconds) || 0) * 1000; // fallback: match time (ms scale)
    };
    const raw = fixtureEvents || [];
    const seenIds = new Set();
    const list = raw.filter((e) => {
      const id =
        e.event_id ??
        `ev-${e.time_in_seconds}-${e.player1_id}-${e.player2_id}-${e.event_key}`;
      if (seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
    list.sort((a, b) => getSortVal(b) - getSortVal(a)); // desc: newest / latest in match first
    const homeTeamId = homeTeam?.team_id;
    const flat = list.flatMap((e) =>
      formatDdsEventForTimeline(e, playerLookup, homeTeamId),
    );
    const seenFlatIds = new Set();
    const deduped = flat.filter((entry) => {
      if (seenFlatIds.has(entry.id)) return false;
      seenFlatIds.add(entry.id);
      return true;
    });
    deduped.sort((a, b) => {
      const ta =
        (a.updatedAt ? new Date(a.updatedAt).getTime() : null) ??
        (a.timeInSeconds ?? 0) * 1000;
      const tb =
        (b.updatedAt ? new Date(b.updatedAt).getTime() : null) ??
        (b.timeInSeconds ?? 0) * 1000;
      return tb - ta; // desc
    });
    return deduped;
  }, [fixtureEvents, playerLookup, homeTeam]);

  const { eventService, publishGoalEvent, brokerStatus, noteCaptionUse } = useEventService({
    isLive,
    isNleEnabled,
    // OPUS-1619 — NLE caption URLs are built from every scene this renderer
    // carries, not just the one TXLive's presets come from: the post graphics
    // live in a different scene from the live ones.
    renderer: selectedRenderer,
    fixtureName,
    onFixtureStateUpdate: updateFixtureState,
  });

  // Kept in a ref so handleMaestroAction above — declared before this hook
  // runs — can reach it without listing it as a dependency.
  useEffect(() => { noteCaptionUseRef.current = noteCaptionUse; }, [noteCaptionUse]);

  // Push the STOMP broker connection state up to the footer status bar.
  useEffect(() => {
    onBrokerStatusChange?.(brokerStatus);
  }, [brokerStatus, onBrokerStatusChange]);

  // Operator edits from EventEditModal go through the plugin pipeline so the
  // bundle (liveEvent + mqItems + optimisticState) is owned by the plugin.
  // The dds.sendLiveEvent driver detects event_op:"update" and routes to
  // DDS updateLiveEvents → DWS PUT /api/event/:event_id. The optimisticState
  // patches the local fixtureEvents array immediately for instant timeline
  // refresh; the DDS subscription confirms shortly after.
  const triggerEventUpdate = useCallback(
    async ({ event_id, fixture_id, competition_id, season_id, changedData, sourceEvent }) => {
      if (!event_id || !fixture_id || !competition_id || !season_id) {
        console.warn("[triggerEventUpdate] missing identity", { event_id, fixture_id, competition_id, season_id });
        return null;
      }
      if (!changedData || Object.keys(changedData).length === 0) {
        console.log("[triggerEventUpdate] no changes, skipping");
        return null;
      }
      // The modal's `sourceEvent` is the timeline-shaped row (no event_key).
      // Look up the original stored doc in fixtureEvents to get event_key —
      // we need it to map to the right plugin (goal / card / sub) and to set
      // card_type / goal_type for plugin variant resolution.
      const storedEvent = fixtureEvents?.find((e) => e.event_id === event_id) || {};
      const eventKey = changedData.event_key || storedEvent.event_key || sourceEvent?.event_key;

      // Map row type → plugin filename (`${type}_event_object_builder.js`).
      let pluginType;
      let typeExtras = {};
      if (sourceEvent?.type === "goal") {
        pluginType = "goal";
        typeExtras = { goal_type: eventKey || "goal" };
      } else if (sourceEvent?.type === "yellow" || sourceEvent?.type === "red") {
        pluginType = "card";
        typeExtras = { card_type: eventKey || (sourceEvent.type === "red" ? "red_card" : "yellow_card") };
      } else if (sourceEvent?.type === "sub") {
        pluginType = "sub";
      } else {
        console.warn("[triggerEventUpdate] unsupported event type", sourceEvent?.type);
        return null;
      }

      const isHome = (storedEvent.team_id || sourceEvent?.team_id)
        ? (storedEvent.team_id || sourceEvent.team_id) === homeTeam?.team_id
        : sourceEvent?.team === "home";

      // Session-observability diff — { field: { from, to } } for exactly the
      // fields the operator changed, so the audit history shows what an
      // "update" actually did instead of just that one happened.
      const sessionChanges = Object.fromEntries(
        Object.entries(changedData).map(([field, to]) => [
          field,
          { from: storedEvent?.[field] ?? sourceEvent?.[field] ?? null, to },
        ])
      );

      // A goal edit re-records the NLE clip (OPUS-1339). The scorer and post
      // scoreline the plugin bakes, and the clip's file name, come from these —
      // the same fields the create path sends. new_score is display only:
      // without persist_score_on_update the plugin writes nothing, and a team
      // change is persisted by the block below.
      let goalDisplay = {};
      if (pluginType === "goal") {
        const eventsAfter = (fixtureEvents || []).map((e) =>
          e.event_id === event_id ? { ...e, ...changedData } : e,
        );
        const scorer = playerLookup.get(
          changedData.player1_id ?? storedEvent.player1_id ?? sourceEvent?.player1_id,
        );
        goalDisplay = {
          new_score: computeOpusGoalScores(eventsAfter, homeTeam?.team_id, awayTeam?.team_id),
          homeTeam,
          awayTeam,
          player_first_name: getTeamFirstName(scorer),
          player_last_name: getEventName(scorer),
          shirt_number: scorer?.shirt_number ?? null,
        };
      }

      // The players the event names after this edit. A sub's player1 went off
      // and player2 came on; goals and cards have the one player.
      const player1Id = changedData.player1_id ?? storedEvent.player1_id ?? sourceEvent?.player1_id;
      const player2Id = changedData.player2_id ?? storedEvent.player2_id ?? sourceEvent?.player2_id;
      const playerDisplay = pluginType === "sub"
        ? { ...playerKeys(playerLookup.get(player1Id), "Off"), ...playerKeys(playerLookup.get(player2Id), "On") }
        : playerKeys(playerLookup.get(player1Id));

      const merged = {
        type: pluginType,
        event_op: "update",
        event_id,
        fixtureId: fixture_id,
        competitionId: competition_id,
        seasonId: season_id,
        event_key: eventKey,
        team_id: changedData.team_id ?? storedEvent.team_id ?? sourceEvent?.team_id,
        team_side: sourceEvent?.team || (isHome ? "home" : "away"),
        player1_id: player1Id,
        player2_id: player2Id,
        ...playerDisplay,
        time_in_seconds:
          changedData.time_in_seconds ??
          storedEvent.time_in_seconds ??
          sourceEvent?.timeInSeconds ??
          0,
        game_period: storedEvent.game_period ?? sourceEvent?.game_period ?? fixture?.fixture_session ?? null,
        ...typeExtras,
        ...goalDisplay,
        profile_id: profileId,
        user_role: userRole,
        rendererId: selectedRenderer?.renderer_id ?? null,
        // Step 1 of the eventService bundle handler applies this via
        // drivers.fixtureState.update — gives the timeline an instant patch
        // before the DDS round-trip lands.
        optimisticState: {
          collection: "event",
          op: "update",
          key: { event_id, fixture_id, competition_id, season_id },
          data: { event_id, ...changedData, updatedAt: new Date().toISOString() },
        },
        // Card stat recomputation fields — passed for all event types; the
        // card builder uses them, others ignore them.
        fixtureEvents: fixtureEvents || [],
        old_team_id: storedEvent?.team_id ?? sourceEvent?.team_id ?? null,
        sessionChanges,
      };
      // Editing one pair of a double sub re-records the clip for the whole
      // group: the other subs of the same team, period and clock second.
      if (pluginType === "sub") {
        const subGroup = subGroupForEdit(fixtureEvents, merged, playerLookup);
        if (subGroup) merged.sub_group = subGroup;
      }
      console.log("[triggerEventUpdate] dispatching to eventService", merged);
      const triggerPromise = eventService?.trigger(merged);

      // Goal team-change side-effects: when an operator reassigns a goal to the
      // other team, the plugin's update path intentionally skips score patches
      // (see goal_event_object_builder.js). Handle it here at the call site —
      // recalc the score, optimistically patch fixture_result, persist via DDS,
      // and snap the renderer's gfx_PermScore via APPLY_DATA.
      if (pluginType === "goal" && changedData.team_id) {
        const oldTeamId = storedEvent.team_id ?? sourceEvent?.team_id;
        const newTeamId = changedData.team_id;
        const oldIsHome = oldTeamId === homeTeam?.team_id;
        const newIsHome = newTeamId === homeTeam?.team_id;
        if (oldTeamId && newTeamId && oldIsHome !== newIsHome) {
          const oldHome = result?.home_team_score || 0;
          const oldAway = result?.away_team_score || 0;
          const newScore = {
            home_team_score: Math.max(0, oldHome + (oldIsHome ? -1 : 0) + (newIsHome ? 1 : 0)),
            away_team_score: Math.max(0, oldAway + (oldIsHome ? 0 : -1) + (newIsHome ? 0 : 1)),
          };

          updateFixtureState?.({
            collection: "fixture_result",
            data: {
              home_team_score: newScore.home_team_score,
              away_team_score: newScore.away_team_score,
            },
          });

          const session = getStoredSession();
          const originSite = session?.username || session?.user_name || "aedrive";
          const addedBy = session?.username || "aedrive";
          const persistScores = async () => {
            for (const { path, value, suffix } of [
              { path: "data.home_team_score", value: newScore.home_team_score, suffix: "score_team_change_home" },
              { path: "data.away_team_score", value: newScore.away_team_score, suffix: "score_team_change_away" },
            ]) {
              const patchId = `P${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${suffix}`;
              const payload = sendPatchesFromDrivePreset({
                collection: "fixture_result",
                originSite,
                op: "PATCH",
                valid_from: new Date().toISOString(),
                added_by: addedBy,
                patch_id: patchId,
                path,
                value,
                extraKey: { fixture_id, competition_id, season_id },
              });
              try {
                await dds.http.request({ query: payload.query, variables: payload.variables });
              } catch (e) {
                console.error("[triggerEventUpdate] fixture_result PATCH failed", path, e);
              }
            }
          };

          persistScores();

          sendApplyDataState?.({
            scores: { home: newScore.home_team_score, away: newScore.away_team_score },
            homeTeam,
            awayTeam,
            renderer: selectedRenderer?.renderer_id ?? rendererId ?? null,
          })?.catch?.((e) => console.error("[triggerEventUpdate] sendApplyDataState failed", e));
        }
      }

      return triggerPromise;
    },
    [eventService, fixture, fixtureEvents, homeTeam, awayTeam, playerLookup, result, profileId, userRole, selectedRenderer, rendererId, updateFixtureState, sendApplyDataState],
  );

  const [activeAction, setActiveAction] = useState(null);
  const [open, setOpen] = useState(false);
  const [formState, setFormState] = useState({});

  const [resolvingRenderer, setResolvingRenderer] = useState(false);

  const [copilotGoalDialogOpen, setCopilotGoalDialogOpen] = useState(false);
  const [copilotGoalTeam, setCopilotGoalTeam] = useState(null);
  // Exact clock second captured the instant '+' was hit — the goal's Time Awarded.
  const [goalClickSeconds, setGoalClickSeconds] = useState(0);
  // Score BEFORE the '+' bump — passed to the logging dialog as the event's
  // old_score so the confirm's goal animation still sees the pre→post delta
  // (the score itself was already written to DDS on the '+' click).
  const [goalPreScore, setGoalPreScore] = useState(null);
  // True once the operator confirms (Animate In) the goal being logged — lets
  // the dialog-close handler skip the optimistic-score rollback on confirm.
  const goalConfirmedRef = useRef(false);
  const [copilotEventsOpen, setCopilotEventsOpen] = useState(false);
  // When set, the copilot EVENTS timeline auto-opens the overwrite dialog for
  // this goal event id — used by "Reassign Goalscorer" to route straight there.
  const [reassignGoalEventId, setReassignGoalEventId] = useState(null);

  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [presetValidity, setPresetValidity] = useState({});
  const [activeLineup, setActiveLineup] = useState("home-players"); // 'home-players', 'home-subs', 'away-players', 'away-subs'

  // OPUS-1527 — operator messages go to the footer notification tray now, not to
  // an overlay that sat on top of the controls and swallowed clicks on them.
  // pushNotification is a module singleton, so a fixture change (which remounts
  // this component via key=) can no longer strand a pending toast timeout holding
  // the dead tree. Aliased rather than replaced at ~20 call sites so every
  // existing call and every onNotify prop stays exactly as it was.
  const showNotification = pushNotification;

  // Wire useFixtureData's onLineupChange to the notification tray. Debouncing /
  // burst-collapsing is owned by useFixtureData's patch-level detector,
  // so this handler fires immediately whenever the hook decides a lineup
  // event is worth surfacing. Present-continuous copy because the panel
  // typically churns for a few seconds after the message appears.
  useEffect(() => {
    lineupChangeHandlerRef.current = (info) => {
      if (info?.cacheOnly) {
        // Pre-clear the sticky entry so pickStickyLineup doesn't hold the
        // stale "last starter moved to subs" frame as degenerate on the
        // next useMemo run. No message — the data hasn't settled yet.
        if (info?.teamId) lineupStickyRef.current.delete(info.teamId);
        return;
      }
      showNotification('Lineup updating from Aether…', 'info');
    };
    return () => { lineupChangeHandlerRef.current = null; };
  }, [showNotification]);

  const [validatingPresets, setValidatingPresets] = useState(false);
  const [shortcutRegistryOpen, setShortcutRegistryOpen] = useState(false);

  useEffect(() => {
    const validatePresets = async () => {
      setValidatingPresets(true);
      const statuses = {};
      const presetsToValidate = presets.filter(
        (p) => presetValidity[p.id] === undefined,
      );

      if (presetsToValidate.length === 0) {
        setValidatingPresets(false);
        return;
      }

      await Promise.all(
        presetsToValidate.map(async (preset) => {
          try {
            if (!preset.captionDefinition?.path) {
              statuses[preset.id] = "invalid";
              return;
            }
            const res = await fetch(preset.captionDefinition.path);
            if (!res.ok) {
              statuses[preset.id] = "invalid";
              return;
            }
            const json = await res.json();
            statuses[preset.id] =
              json && typeof json === "object" && json.schema
                ? "valid"
                : "invalid";
          } catch (e) {
            console.warn(`Failed to validate preset ${preset.label}:`, e);
            statuses[preset.id] = "invalid";
          }
        }),
      );

      setValidatingPresets(false);
      setPresetValidity((prev) => ({ ...prev, ...statuses }));
    };

    if (presets.length > 0) validatePresets();
  }, [presets]);

  // Dynamic Preset Construction (No Fetching)
  useEffect(() => {
    if (
      !selectedRenderer?.scene_path ||
      !Array.isArray(selectedRenderer?.captions)
    ) {
      console.warn("TXLive: Invalid selectedRenderer structure for presets", {
        scene_path: selectedRenderer?.scene_path,
        captions: selectedRenderer?.captions,
      });
      setPresets([]);
      return;
    }

    const baseUrl = process.env.REACT_APP_RENDERER_PREVIEW_URL;

    // Ensure clean path join
    const cleanBase = baseUrl ? baseUrl.replace(/\/+$/, "") : "";
    const cleanScenePath = selectedRenderer.scene_path.replace(/^\/+/, "");

    const dynamicPresets = selectedRenderer.captions.map((cap) => {
      const name = typeof cap === "string" ? cap : cap.name;
      const version =
        typeof cap === "object" && cap.version ? cap.version : "latest";

      // Construct path: baseUrl/scene_path/caption_name/version/caption_name.json
      const path = `${cleanBase}/${cleanScenePath}/${name}/${version}/${name}.json`;

      return {
        id: `preset_dyn_${name}`,
        label: name,
        captionDefinition: {
          id: name,
          name: name,
          path: path,
        },
      };
    });

    if (dynamicPresets.length > 0) {
      setPresets(dynamicPresets);
      if (!activePresetId) setActivePresetId(dynamicPresets[0].id);
    } else {
      setPresets([]);
      setActivePresetId(null);
      setPresetDialogOpen(false);
    }
  }, [selectedRenderer]);

  const [substitutionsDialogOpen, setSubstitutionsDialogOpen] = useState(false);

  // Copilot clock-control dialogs — parity with the operator clock menu.
  const [copilotClockTimeOpen, setCopilotClockTimeOpen] = useState(false);
  const [copilotClockConstraints, setCopilotClockConstraints] = useState({ min: undefined, max: undefined });
  const [copilotResetConfirmOpen, setCopilotResetConfirmOpen] = useState(false);
  // Clock-override confirm: null = closed; otherwise the pending start params
  // ({ eventKey, fixtureSession, period, periodLabel, brokerAction, brokerTime,
  // refClock }) captured when the operator starts an already-running clock.
  const [clockOverride, setClockOverride] = useState(null);


  const [focusedElement, setFocusedElement] = useState("homeLineup");
  const homeLineupRef = useRef(null);
  const statsRef = useRef(null);
  const awayLineupRef = useRef(null);
  const presetsRef = useRef(null);
  const homePlayersListRef = useRef(null);
  const awayPlayersListRef = useRef(null);


  const panelRefs = {
    homeLineup: homeLineupRef,
    stats: statsRef,
    awayLineup: awayLineupRef,
    presets: presetsRef,
  };

  useEffect(() => {
    if (!clockTime) return;
    const [m, s] = clockTime.split(":").map(Number);
    const totalSeconds = m * 60 + s;
    const fhLimit = clockProfile.firstHalfLimit;
    const shLimit = clockProfile.secondHalfLimit;
    // Profiles with no real period boundaries (e.g. football_pls uses sentinel
    // limits) can't derive period from clock time — explicit handlers own it.
    if (fhLimit === shLimit) return;
    if (totalSeconds === 0) {
      if (period !== "PRE-GAME") setPeriod("PRE-GAME");
    } else if (totalSeconds < fhLimit) {
      if (period !== "FIRST HALF") setPeriod("FIRST HALF");
    } else if (totalSeconds === fhLimit) {
      if (period !== "HALF TIME") setPeriod("HALF TIME");
    } else if (totalSeconds < shLimit) {
      if (period !== "SECOND HALF") setPeriod("SECOND HALF");
    } else {
      if (period !== "FULL TIME") setPeriod("FULL TIME");
    }
  }, [clockTime, period, clockProfile]);

  // Period can arrive via an external feed sync (fixture_status) with no
  // matching clock_* event, so clockTime/the effect above can get stuck at
  // Pre-Game. One-time seed only (periodSeededRef) — never re-fires, so it
  // can't later override an operator's own Reset/Start.
  const periodSeededRef = useRef(false);
  useEffect(() => {
    if (periodSeededRef.current) return;
    const status = String(fixture?.fixture_status ?? "").trim().toLowerCase();
    let derived = null;
    if (FINISHED_STATUSES.has(status)) {
      derived = "FULL TIME";
    } else if (HALF_TIME_STATUSES.has(status)) {
      derived = "HALF TIME";
    } else if (IN_PROGRESS_STATUSES.has(status)) {
      if (fixture?.fixture_session === 1) derived = "FIRST HALF";
      else if (fixture?.fixture_session === 2) derived = "SECOND HALF";
    }
    if (derived) {
      periodSeededRef.current = true;
      setPeriod(derived);
    }
  }, [fixture?.fixture_status, fixture?.fixture_session]);

  useEffect(() => {
    const masterId =
      rendererId ||
      (() => {
        const stored = JSON.parse(
          localStorage.getItem("rendererSelection") || "{}",
        );
        return stored?.master;
      })();
    if (!masterId) {
      setSelectedRenderer(null);
      setActivePresetId(null);
      setPresetDialogOpen(false);
      return;
    }
    setSelectedRenderer(null);
    setActivePresetId(null);
    setPresetDialogOpen(false);
    let cancelled = false;
    const loadSelectedRenderer = async () => {
      setResolvingRenderer(true);
      try {
        const session = getStoredSession();
        let rendererFromList = null;
        if (session?.orgId) {
          const renderersResponse = await apiService.getRenderersByOrganization(
            session.orgId,
          );
          const renderersList = renderersResponse.renderers || [];
          rendererFromList = renderersList.find(
            (r) => r.id === masterId || r.renderer_id === masterId,
          );

          // Check if rendererFromList has captions. If not, we need to fetch details.
          if (
            rendererFromList &&
            rendererFromList.captions &&
            Array.isArray(rendererFromList.captions)
          ) {
            if (!cancelled) setSelectedRenderer(rendererFromList);
            return;
          }
        }

        // Use renderer_id if available from list, otherwise fallback to masterId
        const idToFetch = rendererFromList?.renderer_id || masterId;
        const response = await apiService.getRendererDetails(idToFetch);

        const details = response?.renderer || response;

        // Merge basics (like renderer_id) from list with details (like captions)
        const finalRenderer = {
          ...rendererFromList,
          ...details,
          renderer_id:
            details.renderer_id || rendererFromList?.renderer_id || details.id,
        };

        if (!cancelled) setSelectedRenderer(finalRenderer);
      } catch (error) {
        if (!cancelled) console.warn("Could not load selected renderer", error);
      } finally {
        if (!cancelled) setResolvingRenderer(false);
      }
    };
    loadSelectedRenderer();
    return () => {
      cancelled = true;
    };
  }, [rendererId]);

  // Maestro dispatches to programRendererUrl over its rendererId prop, so keep
  // it on the renderer selected here. DriveMaestro is keyed on the renderer
  // below, so its next mount reads this value.
  useEffect(() => {
    if (selectedRenderer?.renderer_id) writeProgramRendererUrl(selectedRenderer.renderer_id);
  }, [selectedRenderer?.renderer_id]);

  useEffect(() => {
    if (homePlayersListRef.current?.focusFirst) {
      setTimeout(() => {
        homePlayersListRef.current.focusFirst();
      }, 100);
    }
  }, []);

  // KEYBOARD NAV
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.isContentEditable
      )
        return;

      if (
        presetDialogOpen ||
        open
      )
        return;

      if (
        ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
      ) {
        let currentFocus = focusedElement;
        if (currentFocus !== "homeLineup" && currentFocus !== "awayLineup") {
          currentFocus = "homeLineup";
          setFocusedElement("homeLineup");
          setTimeout(() => homeLineupRef.current?.focus(), 0);
        }

        if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
          const isInsideList = event.target.closest(
            '[data-player-list="true"]',
          );
          const isOnPanel = event.target === panelRefs[currentFocus]?.current;
          if (!isOnPanel && !isInsideList) return;
          event.preventDefault();
          const map = {
            homeLineup: { ArrowRight: "awayLineup" },
            awayLineup: { ArrowLeft: "homeLineup" },
          };
          const next = map[currentFocus]?.[event.key];
          if (next) {
            let idx = null;
            if (currentFocus === "homeLineup")
              idx = homePlayersListRef.current?.getFocusedIndex?.();
            if (currentFocus === "awayLineup")
              idx = awayPlayersListRef.current?.getFocusedIndex?.();
            setFocusedElement(next);
            setTimeout(() => {
              const ref =
                next === "homeLineup" ? homePlayersListRef : awayPlayersListRef;
              if (ref.current) {
                if (idx !== null && ref.current.focusIndex)
                  ref.current.focusIndex(idx);
                else ref.current.focusFirst?.();
              }
            }, 0);
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    focusedElement,
    presetDialogOpen,
    open,
  ]);

  // Drop DOM focus when this window loses OS focus (e.g. clicking into the
  // renderer PiP preview), so a later Enter back in the main UI can't
  // re-trigger whatever button was last clicked.
  useEffect(() => {
    const handleWindowBlur = () => {
      document.activeElement?.blur?.();
    };
    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
  }, []);

  // DriveMaestro (@ae/maestrov2-form-lib) re-focuses a shortcut tile
  // (CaptionButton) after handling its click, so it stays activatable by a
  // later stray Enter/Space — :focus-visible reports true even though it was
  // a mouse click, so that check can't tell the two apart here. These tiles
  // are only meant to fire via their own single physical key, never
  // Tab+Enter, so just block Enter/Space outright whenever a tile inside
  // presetsRef (the DriveMaestro wrapper) holds focus, and blur it after so
  // the focus ring doesn't linger looking "armed". Scoped to this panel only
  // — inputs, dialogs, and every other button in the app are untouched.
  useEffect(() => {
    const handleMaestroEnter = (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const container = presetsRef.current;
      const active = document.activeElement;
      if (!container || !active || !container.contains(active)) return;
      if (typeof active.matches !== "function") return;
      if (!active.matches('button, [role="button"]')) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      active.blur();
    };
    window.addEventListener("keydown", handleMaestroEnter, true);
    return () => window.removeEventListener("keydown", handleMaestroEnter, true);
  }, []);

  // MEMOIZED DATA
  const events = useMemo(
    () => fixtureState?.fixture?.[0]?.event || [],
    [fixtureState],
  );
  // Sticky-last-good cache for each team's lineup split. Keyed by team_id
  // so a fixture change starts from scratch and Leeds vs Newcastle don't
  // share state. See pickStickyLineup for the degenerate-frame logic.
  const lineupStickyRef = useRef(new Map());
  const homeLineup = useMemo(() => {
    const next = buildLineup(homeTeam, events);
    const teamId = homeTeam?.team_id;
    if (!teamId) return next;
    const cache = lineupStickyRef.current;
    const result = pickStickyLineup(cache.get(teamId), next);
    cache.set(teamId, result);
    return result;
  }, [homeTeam, events]);
  const awayLineup = useMemo(() => {
    const next = buildLineup(awayTeam, events);
    const teamId = awayTeam?.team_id;
    if (!teamId) return next;
    const cache = lineupStickyRef.current;
    const result = pickStickyLineup(cache.get(teamId), next);
    cache.set(teamId, result);
    return result;
  }, [awayTeam, events]);

  // Lineup validation errors for both teams, collected centrally so the copilot
  // footer can surface them via the eye-icon modal (instead of inline warnings
  // at the bottom of each lineup). One entry per issue, per team.
  const lineupErrors = useMemo(() => {
    const hasCaptain = (p) => {
      const v = String(p?.is_captain ?? p?.isCaptain ?? p?.captain ?? "no").trim().toLowerCase();
      return v === "yes" || v === "true" || p?.is_captain === true || p?.isCaptain === true;
    };
    const hasShirt = (p) =>
      (p?.identifierNumber ?? p?.shirt_number ?? p?.number ?? "") !== "";
    const errs = [];
    const check = (lineup, name) => {
      const team = name || "Team";
      const starters = lineup?.players || [];
      const all = [...starters, ...(lineup?.subs || [])];
      if (all.length === 0) return; // no lineup loaded yet — nothing to validate
      if (starters.length !== 11) {
        errs.push({
          team,
          severity: "error",
          message: `Lineup has ${starters.length} player${starters.length === 1 ? "" : "s"} — 11 required`,
        });
      }
      if (!all.some(hasCaptain)) {
        errs.push({ team, severity: "warning", message: "No captain assigned" });
      }
      const missing = all.filter((p) => !hasShirt(p)).length;
      if (missing > 0) {
        errs.push({
          team,
          severity: "warning",
          message: `${missing} player${missing === 1 ? "" : "s"} missing a shirt number`,
        });
      }
    };
    check(homeLineup, homeTeam?.team_short_name || homeTeam?.team_name);
    check(awayLineup, awayTeam?.team_short_name || awayTeam?.team_name);
    return errs;
  }, [homeLineup, awayLineup, homeTeam, awayTeam]);

  // Push the collected errors up to the footer (copilot eye icon + modal).
  useEffect(() => {
    onLineupErrorsChange?.(lineupErrors);
  }, [lineupErrors, onLineupErrorsChange]);

  // Substitutions already logged per team — drives the "subs made / left"
  // counter in the substitution dialog. One 'sub' or 'concussion_sub' event
  // === one substitution.
  const isSubEventKey = (key) => key === 'sub' || key === 'concussion_sub';
  const homeSubsMade = useMemo(() => {
    const teamId = homeTeam?.team_id;
    return !teamId
      ? 0
      : events.filter((e) => isSubEventKey(e.event_key?.toLowerCase()) && e.team_id === teamId).length;
  }, [events, homeTeam]);
  const awaySubsMade = useMemo(() => {
    const teamId = awayTeam?.team_id;
    return !teamId
      ? 0
      : events.filter((e) => isSubEventKey(e.event_key?.toLowerCase()) && e.team_id === teamId).length;
  }, [events, awayTeam]);

  // Concussion subs are exempt from the 5-sub cap — a confirmed concussion
  // sub by EITHER team grants both teams one extra permitted substitution
  // (the opposing team is compensated too, per the actual law). The shared
  // bump is the max of the two teams' own concussion-sub counts, not the sum.
  const homeConcussionSubs = useMemo(() => {
    const teamId = homeTeam?.team_id;
    return !teamId
      ? 0
      : events.filter((e) => e.event_key?.toLowerCase() === 'concussion_sub' && e.team_id === teamId).length;
  }, [events, homeTeam]);
  const awayConcussionSubs = useMemo(() => {
    const teamId = awayTeam?.team_id;
    return !teamId
      ? 0
      : events.filter((e) => e.event_key?.toLowerCase() === 'concussion_sub' && e.team_id === teamId).length;
  }, [events, awayTeam]);

  // A player who has ever been subbed off (player1_id on any sub /
  // concussion_sub event) can never be selected again in the Substitutions
  // dialog, permanently for the rest of the match. Derived straight from
  // events rather than homeLineup.subs' is_starting flag so multi-hop chains
  // (a substitute who is later subbed off again) are covered too, and stays
  // correct across the dialog's own open/close state resets.
  const homeIneligiblePlayerIds = useMemo(() => {
    const teamId = homeTeam?.team_id;
    if (!teamId) return [];
    return events
      .filter((e) => isSubEventKey(e.event_key?.toLowerCase()) && e.team_id === teamId)
      .map((e) => e.player1_id)
      .filter(Boolean);
  }, [events, homeTeam]);
  const awayIneligiblePlayerIds = useMemo(() => {
    const teamId = awayTeam?.team_id;
    if (!teamId) return [];
    return events
      .filter((e) => isSubEventKey(e.event_key?.toLowerCase()) && e.team_id === teamId)
      .map((e) => e.player1_id)
      .filter(Boolean);
  }, [events, awayTeam]);

  const [fixtureDuplicates, setFixtureDuplicates] = useState([]);
  const [fixtureDuplicatesOpen, setFixtureDuplicatesOpen] = useState(false);
  const fixtureDuplicatesShownFor = useRef(null);
  useEffect(() => {
    if (!fixtureId) return;
    const homePlayers = [...(homeLineup.players || []), ...(homeLineup.subs || [])];
    const awayPlayers = [...(awayLineup.players || []), ...(awayLineup.subs || [])];
    if (homePlayers.length === 0 && awayPlayers.length === 0) return;
    if (fixtureDuplicatesShownFor.current === fixtureId) return;
    const dups = [
      ...findSquadDuplicates(homePlayers, homeTeam?.team_short_name || homeTeam?.team_tricode || "Home"),
      ...findSquadDuplicates(awayPlayers, awayTeam?.team_short_name || awayTeam?.team_tricode || "Away"),
    ];
    if (dups.length) {
      setFixtureDuplicates(dups);
      setFixtureDuplicatesOpen(true);
    }
    fixtureDuplicatesShownFor.current = fixtureId;
  }, [fixtureId, homeLineup, awayLineup, homeTeam, awayTeam]);
  const teamStats = useMemo(() => {
    return buildTeamStats(homeTeam?.stats || [], awayTeam?.stats || [], getCachedStatsConfig() ?? undefined);
  }, [homeTeam, awayTeam]);

  // Copilot STATS tiles are sourced from the MatchStats_* JSONata captions of
  // the gfx_PermScore graphic. Its caption endpoint is the graphic path's parent
  // dir, so we reuse the already-built preset path for gfx_PermScore.
  const statGraphicDef = useMemo(() => {
    const p = presets.find(
      (x) =>
        x.id === STAT_GRAPHIC ||
        x.id === `preset_dyn_${STAT_GRAPHIC}` ||
        x.captionDefinition?.name === STAT_GRAPHIC ||
        String(x.id).endsWith(`_${STAT_GRAPHIC}`),
    );
    return p?.captionDefinition || null;
  }, [presets]);
  const matchStatsTiles = useMatchStatsTiles({
    graphicPath: statGraphicDef?.path,
    bindingContext: fixtureState,
    enabled: isCopilot && hasFixtureSelected,
  });

  // Headless "click → animate": clicking a stat tile fires the caption's
  // Animate-In straight to the renderer (via a hidden MaestroProvider), instead
  // of opening the Maestro modal. `nonce` bumps per click so re-clicking the
  // same tile re-animates.
  const animateNonceRef = useRef(0);
  const [statAnimateTarget, setStatAnimateTarget] = useState({ caption: null, nonce: 0, mode: "IN" });
  // The caption whose graphic is currently animated IN (null = none). Drives the
  // tile highlight and the click toggle: clicking the live tile animates it OUT.
  // Seeded/reconciled from the real broker state (below) so it survives refresh.
  const [statInCaption, setStatInCaption] = useState(null);
  const lastStatClickRef = useRef(0);

  // Actual on-air state, derived from the broker command state the app already
  // tracks live (useBrokerSubscription). We key on the MatchStats-specific
  // actions (MATCH_STATS_IN/OUT) — the gfx_PermScore layer is shared with the
  // clock/score graphic, which fires the generic IN/OUT.
  const onAirStats = useOnAirStatsCaption({
    actionState,
    dataState,
    graphicId: STAT_GRAPHIC,
    single: matchStatsTiles.single,
    double: matchStatsTiles.double,
  });

  // We can't reliably reverse the *specific* stat from the broker's merged data
  // (titles accumulate/differ), so we remember the caption WE put up, per
  // renderer, in localStorage — gated by the broker's reliable isOnAir. This
  // survives refresh and clears when the graphic actually leaves air.
  const rendererKey = selectedRenderer?.renderer_id || rendererId;
  const STAT_STORE = "opusStatInCaption";
  const storeOnAir = useCallback((caption) => {
    try {
      if (!rendererKey) return;
      if (caption) localStorage.setItem(`${STAT_STORE}:${rendererKey}`, caption);
      else localStorage.removeItem(`${STAT_STORE}:${rendererKey}`);
    } catch { /* localStorage unavailable — non-fatal */ }
  }, [rendererKey]);
  const readStoredOnAir = useCallback(() => {
    try { return rendererKey ? localStorage.getItem(`${STAT_STORE}:${rendererKey}`) : null; } catch { return null; }
  }, [rendererKey]);

  // Reconcile the highlight with the real broker state, unless the operator just
  // clicked (give the renderer a moment to reflect it). Because gfx_PermScore is
  // a shared layer, we act ONLY on MatchStats-specific signals:
  //  - MATCH_STATS_OUT  → the stats graphic was explicitly taken off → clear.
  //  - MATCH_STATS_IN with a broker-identified caption → follow it (this is how
  //    we pick up a stat swapped in via another path / operator).
  //  - anything else (generic clock/score IN, unidentified) → don't clear; just
  //    restore our own remembered caption if we have none (survives refresh).
  useEffect(() => {
    if (Date.now() - lastStatClickRef.current < 5000) return;
    if (onAirStats.matchStatsOff) {
      setStatInCaption(null);
      storeOnAir(null);
      return;
    }
    if (onAirStats.matchStatsOnAir && onAirStats.onAirCaption) {
      setStatInCaption(onAirStats.onAirCaption);
      return;
    }
    const stored = readStoredOnAir();
    if (stored) setStatInCaption((cur) => cur || stored);
  }, [onAirStats.matchStatsOnAir, onAirStats.matchStatsOff, onAirStats.onAirCaption, readStoredOnAir, storeOnAir]);

  // OPUS-1327 — stat tiles open Maestro, so the on-air tile (green dot) follows
  // the MatchStats IN/OUT the operator takes there.
  const noteStatAction = useCallback((cmd) => {
    const caption = statOnAirChange(cmd, STAT_GRAPHIC);
    if (caption === undefined) return;
    lastStatClickRef.current = Date.now();
    setStatInCaption(caption);
    storeOnAir(caption);
  }, [storeOnAir]);
  useEffect(() => { noteStatActionRef.current = noteStatAction; }, [noteStatAction]);


  // "Deanimate Clock Stats" — animate OUT whatever stats graphic is on air, with
  // that caption's own *_OUT. Prefer the known-live caption, then the one the
  // broker reports (advanced stats each have their own); else any MatchStats tile.
  const handleClearStatsGraphic = useCallback(() => {
    const caption =
      statInCaption ||
      onAirStats.onAirCaption ||
      matchStatsTiles.single?.[0]?.name ||
      matchStatsTiles.double?.[0]?.name ||
      "MatchStats";
    lastStatClickRef.current = Date.now();
    animateNonceRef.current += 1;
    setStatAnimateTarget({ caption, nonce: animateNonceRef.current, mode: "OUT", formData: null });
    setStatInCaption(null);
    storeOnAir(null);
  }, [statInCaption, onAirStats.onAirCaption, matchStatsTiles.single, matchStatsTiles.double, storeOnAir]);
  const scoreData = useMemo(
    () => ({
      home: {
        team: homeTeam?.team_tricode,
        score: result?.home_team_score ?? 0,
        status: "GOAL",
      },
      away: {
        team: awayTeam?.team_tricode,
        score: result?.away_team_score ?? 0,
        status: "GOAL",
      },
      period: period,
      timer: clockTime,
      competition: fixtureState?.competition?.[0]?.competition_name,
      // Reuse the resolved profileId rather than re-deriving from fixtureState:
      // this was a second, independent copy of the same expression that missed
      // the fallback entirely, so it could emit null while profileId did not.
      profile_id: profileId,
    }),
    [homeTeam, awayTeam, result, clockTime, period, fixtureState, profileId],
  );

  const driveCaptions = useMemo(
    () =>
      presets.map((p) => ({
        id: p.id,
        label: p.label,
        captionDefinition: p.captionDefinition,
      })),
    [presets],
  );

  const handleDriveInvalid = useCallback(() => {
    showNotification("This caption is not Maestro V2 compatible", "error");
  }, [showNotification]);

  const noopFormDataChange = useCallback(() => {}, []);

  const { registryItems: shortcutRegistryItems, keyByPresetId } =
    usePresetShortcuts(presets, isCopilot ? undefined : (buttonId) => {
      setActivePresetId(buttonId);
      setPresetDialogOpen(true);
    });

  useEffect(() => {
    if (
      presets.length > 0 &&
      activePresetId &&
      !presets.find((p) => p.id === activePresetId) &&
      !shortcutRegistryItems?.find((item) => item.id === activePresetId)
    ) {
      setActivePresetId(null);
      setPresetDialogOpen(false);
    }
  }, [presets, activePresetId, shortcutRegistryItems]);

  // --- HANDLERS ---

  // Clock Handlers
  const triggerClockAction = useCallback(
    (action, time, dir) => {
      const rid = getRendererIdToUse();
      apiClockAction(action, time, dir, rid);
      showNotification(`Clock ${action} command sent`, "success");
    },
    [getRendererIdToUse, apiClockAction],
  );

  const patchFixturePeriodAndClock = useCallback(async (fields) => {
    if (!selectedFixtureId) return;
    if (!fields || Object.keys(fields).length === 0) return;

    updateFixtureState({ collection: "fixture", data: { ...fields } });

    try {
      const session = getStoredSession();
      const baseExtra = {
        fixture_id: selectedFixtureId,
        competition_id: competitionId,
        season_id: seasonId,
      };
      const baseMeta = {
        collection: "fixture",
        originSite: session?.username || session?.user_name || "Op1",
        op: "PATCH",
        valid_from: new Date().toISOString(),
        added_by: session?.username || "Op1",
        extraKey: baseExtra,
      };

      await Promise.all(
        Object.entries(fields).map(([key, value]) => {
          const payload = sendPatchesFromDrivePreset({
            ...baseMeta,
            patch_id: `P${Date.now()}_${Math.random().toString(36).slice(2, 9)}_${key}`,
            path: `data.${key}`,
            value,
          });
          return dds.http.request({
            query: payload.query,
            variables: payload.variables,
          });
        }),
      );
    } catch (e) {
      console.error("Failed to patch fixture session/clock_status in DDS", e);
    }
  }, [selectedFixtureId, competitionId, seasonId, updateFixtureState]);

  const sessionSeededRef = useRef(false);
  useEffect(() => {
    if (loading || sessionSeededRef.current || !fixture) return;
    sessionSeededRef.current = true;
    if (fixture.fixture_session === undefined && fixture.clock_status === undefined) {
      patchFixturePeriodAndClock({ fixture_session: null, clock_status: 0 });
    }
  }, [loading, fixture, patchFixturePeriodAndClock]);

  // Clock event triggers — the matching clock_*_event_object_builder in
  // BB_EventService consumes (fixture_session, clock_status, opta_match_status)
  // to derive the new fixture_status via the matrix and emits the DDS patches
  // for all three fields. Optimistic state mirrors what the builder will
  // persist so the local timeline reflects the change immediately.
  const triggerClockEvent = useCallback(async (eventKey, clockState = {}) => {
    if (!eventService) return;
    const session = getStoredSession();
    const parseSeconds = (str) => {
      if (!str) return 0;
      const [m, s] = String(str).split(":").map(Number);
      if (Number.isNaN(m)) return 0;
      return m * 60 + (Number.isNaN(s) ? 0 : s);
    };
    const fixtureSession = clockState.fixtureSession ?? null;
    const clockStatus = clockState.clockStatus ?? 0;
    try {
      await eventService.trigger({
        type: eventKey,
        event_op: 'create',
        renderer: getRendererIdToUse?.(),
        fixtureId: selectedFixtureId,
        competitionId,
        seasonId,
        fixture_id: selectedFixtureId,
        competition_id: competitionId,
        season_id: seasonId,
        game_period: fixtureSession,
        fixture_session: fixtureSession,
        clock_status: clockStatus,
        // Clock override: when restarting an already-running clock, the caller
        // passes the server-anchored instant the operator CLICKED start, so the
        // clock runs from the click (not the override-confirm). DDS preserves a
        // supplied ref_clock (OPUS-1172); a normal start omits it and DDS stamps
        // the server-receive time.
        ...(clockState.refClock ? { ref_clock: clockState.refClock } : {}),
        opta_match_status: fixture?.opta_match_status ?? null,
        homeTeam,
        awayTeam,
        home_team_score: result?.home_team_score ?? 0,
        away_team_score: result?.away_team_score ?? 0,
        // Ident strap lines (gfx_LF_Ident strTitle / strVenue). Only
        // clock_start_1h / clock_start_2h read these; the other clock_*
        // builders ignore them.
        venue_name: fixture?.venue?.[0]?.venue_name ?? '',
        competition_name: fixtureState?.competition?.[0]?.competition_name ?? '',
        // Scorers for the half/full-time scoreline. Only clock_end_1h /
        // clock_end_2h read these; the other clock_* builders ignore them.
        homeScorers: buildScorersByTeam(fixtureEvents, playerLookup, homeTeam?.team_id),
        awayScorers: buildScorersByTeam(fixtureEvents, playerLookup, awayTeam?.team_id),
        // OPUS-1176: previously clock_match_reset read the whole timeline here
        // and emitted a deleteLiveEvent PER ROW — N sequential HTTP mutations,
        // each echoing back through the WS subscription (re-render + logs). On a
        // long timeline that was the "reset = console/network chaos". The wipe
        // now goes through the single server-side resetFixturePreset(deleteBase)
        // call in the reset flows, so we send no per-row deletes. (Other clock_*
        // builders ignore this field.)
        fixtureEvents: [],
        optimisticState: {
          collection: 'fixture',
          // OPUS-1505 — the key is REQUIRED, not decoration. applyStaticDataPatch's
          // patchSeasonFixtureResult and patchSeasonFixture both do
          // `if (!fixtureId) return` before touching season[].fixture[], so an
          // optimistic patch without one updates the flat root.fixture_result[0]
          // and leaves the NESTED season.fixture[].fixture_result untouched. The
          // header score moved while season.fixture[] still read null until the
          // server round-trip landed. The sibling `event` optimistic patch has
          // always carried a key; these did not.
          key: { fixture_id: fixtureId },
          data: { fixture_session: fixtureSession, clock_status: clockStatus },
        },
        // Overwrite Clock passes the operator-typed time explicitly — clockTime
        // (component state) may not have caught up with what was just typed yet.
        time_in_seconds: clockState.timeInSeconds ?? parseSeconds(clockTime),
        triggered_by: session?.username || session?.user_name || null,
        profile_id: profileId,
        user_role: userRole,
      });
    } catch (e) {
      console.error(`Failed to trigger ${eventKey} event`, e);
    }
    // The clock_* builders now create the timeline row (non-null liveEvent),
    // so no separate marker write is needed here.
  }, [eventService, getRendererIdToUse, selectedFixtureId, competitionId, seasonId, clockTime, profileId, userRole, fixture, fixtureState, fixtureEvents, homeTeam, awayTeam, result, playerLookup]);

  const copilotTabControlRef = useRef(null);

  const handleClockOptionSelect = useCallback(async (option) => {
    if (option.id === "reset") {
      setCopilotResetConfirmOpen(true);
      return;
    }
    if (option.id === "overwrite") {
      let min;
      let max;
      if (period === "FIRST HALF") {
        min = 0;
        max = Math.ceil(clockProfile.firstHalfLimit / 60);
      } else if (period === "SECOND HALF") {
        min = 45;
        max = Math.ceil(clockProfile.secondHalfLimit / 60);
      }
      setCopilotClockConstraints({ min, max });
      setCopilotClockTimeOpen(true);
      return;
    }
    if (option.id === "startFirstHalf") {
      // Override guard: if a clock_start_1h already exists the clock is already
      // running — confirm before restarting it, and capture the click instant
      // (server-anchored) so the restart runs from the click, not the confirm.
      const exists1 = (fixtureEvents || []).some((e) => (e.event_key || e.event_type) === 'clock_start_1h');
      if (exists1) {
        setClockOverride({ eventKey: 'clock_start_1h', fixtureSession: 1, period: 'FIRST HALF', periodLabel: 'First Half', brokerAction: 'START', brokerTime: '00:00', refClock: new Date(getServerNow()).toISOString() });
        return;
      }
      copilotTabControlRef.current?.switchTab("IN-MATCH");
      setPeriod("FIRST HALF");
      await triggerClockAction("START", "00:00", "UP");
      await triggerClockEvent('clock_start_1h', { fixtureSession: 1, clockStatus: 1 });
    }
    if (option.id === "endFirstHalf") {
      copilotTabControlRef.current?.switchTab("HT/FT");
      setPeriod("HALF TIME");
      await triggerClockAction("STOP_1ST_HALF");
      const endSeconds = getPeriodEndSeconds(fixtureEvents, 1, clockProfile.firstHalfEndSeconds, clockProfile.secondHalfEndSeconds);
      await triggerClockEvent('clock_end_1h', { fixtureSession: 1, clockStatus: 0, timeInSeconds: endSeconds });
    }
    if (option.id === "startSecondHalf") {
      const exists2 = (fixtureEvents || []).some((e) => (e.event_key || e.event_type) === 'clock_start_2h');
      if (exists2) {
        setClockOverride({ eventKey: 'clock_start_2h', fixtureSession: 2, period: 'SECOND HALF', periodLabel: 'Second Half', brokerAction: 'START_2ND_HALF', brokerTime: '45:00', refClock: new Date(getServerNow()).toISOString() });
        return;
      }
      copilotTabControlRef.current?.switchTab("IN-MATCH");
      setPeriod("SECOND HALF");
      await triggerClockAction("START_2ND_HALF", "45:00", "UP");
      await triggerClockEvent('clock_start_2h', { fixtureSession: 2, clockStatus: 1 });
    }
    if (option.id === "endSecondHalf") {
      copilotTabControlRef.current?.switchTab("HT/FT");
      setPeriod("FULL TIME");
      await triggerClockAction("STOP_2ND_HALF");
      const endSeconds = getPeriodEndSeconds(fixtureEvents, 2, clockProfile.firstHalfEndSeconds, clockProfile.secondHalfEndSeconds);
      await triggerClockEvent('clock_end_2h', { fixtureSession: 2, clockStatus: 0, timeInSeconds: endSeconds });
    }
  }, [setPeriod, triggerClockAction, triggerClockEvent, period, clockProfile, fixtureEvents, getServerNow]);

  // Override-confirm handler: restart the already-running clock from the instant
  // the operator clicked start (captured in clockOverride.refClock), which DDS
  // preserves so deriveClockModel ticks from the click. The old clock_start
  // stays in the event log; the newer ref_clock simply wins.
  const doClockOverride = useCallback(async () => {
    const o = clockOverride;
    if (!o) return;
    setClockOverride(null);
    setPeriod(o.period);
    if (o.period === "FIRST HALF" || o.period === "SECOND HALF") {
      copilotTabControlRef.current?.switchTab("IN-MATCH");
    }
    await triggerClockAction(o.brokerAction, o.brokerTime, "UP");
    await triggerClockEvent(o.eventKey, { fixtureSession: o.fixtureSession, clockStatus: 1, refClock: o.refClock });
  }, [clockOverride, setPeriod, triggerClockAction, triggerClockEvent]);

  const doCopilotClockReset = useCallback(async () => {
    setCopilotResetConfirmOpen(false);
    copilotTabControlRef.current?.switchTab("PRE-MATCH");
    setPeriod("PRE-GAME");
    await triggerClockEvent('clock_match_reset', { fixtureSession: null, clockStatus: 0 });
    await triggerClockAction("RESET");
    try {
      await sendApplyDataState?.({
        scores: { home: 0, away: 0 },
        homeTeam,
        awayTeam,
        redCardTabs: { home: 0, away: 0 },
      });
    } catch (e) {
      console.error('Failed to reset broker score', e);
    }
    // OPUS-1176: wipe the timeline with ONE server-side mutation instead of the
    // old per-row deleteLiveEvent fan-out (the source of the reset chaos). This
    // copilot path had no server-side wipe before — it relied entirely on those
    // per-row deletes. Mirrors the full reset in StatsPanel.
    if (selectedFixtureId) {
      try {
        const resetPreset = resetFixturePreset({
          fixture_id: selectedFixtureId,
          ...(competitionId && { competition_id: competitionId }),
          ...(seasonId && { season_id: seasonId }),
          reason: 'operator_reset',
          deleteBase: true,
        });
        await dds.http.request({ query: resetPreset.query, variables: resetPreset.variables });
      } catch (e) {
        console.error('Failed to reset fixture timeline', e);
      }
    }
  }, [setPeriod, triggerClockEvent, triggerClockAction, sendApplyDataState, homeTeam, awayTeam, selectedFixtureId, competitionId, seasonId]);

  const handleCopilotClockTimeConfirm = useCallback(async (timeString, totalSeconds) => {
    setCopilotClockTimeOpen(false);
    copilotTabControlRef.current?.switchTab("IN-MATCH");
    const minutes = totalSeconds / 60;
    let nextSession;
    if (minutes <= 45) {
      setPeriod('FIRST HALF');
      nextSession = 1;
    } else {
      setPeriod('SECOND HALF');
      nextSession = 2;
    }
    await triggerClockAction('START', timeString);
    // Dedicated clock_overwrite event (not clock_start_1h/2h) — routes through
    // triggerClockEvent so clock_status/fixture_status get derived and the
    // timeline marker gets written, same as every other clock action, while
    // correctly labeling the timeline entry "Overwrite Clock" instead of a
    // misleading "Start First/Second Half".
    await triggerClockEvent('clock_overwrite', {
      fixtureSession: nextSession,
      clockStatus: 1,
      timeInSeconds: totalSeconds,
      refClock: new Date(getServerNow()).toISOString(),
    });
  }, [setPeriod, triggerClockAction, triggerClockEvent, getServerNow]);

  // Held above the keyed caption-window mount: that remounts whenever the
  // operator opens a different graphic, which would close the developer strip
  // every time they switched.
  const copilotDeveloper = useDeveloperPanel();

  const controlsContainerRef = useRef(null);

  const copilotTabControl = useCopilotTabControl({
    driveCaptions,
    clockHandler: handleClockOptionSelect,
    tabContainerRef: controlsContainerRef,
    onMiss: (graphic) =>
      showNotification(`No graphic "${graphic}" on this renderer`, "error"),
  });
  copilotTabControlRef.current = copilotTabControl;

  const handleStatsPanelClockOptionSelect = useCallback((option) => {
    if (option?.id === "startFirstHalf" || option?.id === "startSecondHalf") {
      copilotTabControlRef.current?.switchTab("IN-MATCH");
    } else if (option?.id === "endFirstHalf" || option?.id === "endSecondHalf") {
      copilotTabControlRef.current?.switchTab("HT/FT");
    } else if (option?.id === "reset") {
      copilotTabControlRef.current?.switchTab("PRE-MATCH");
    }
  }, []);

  const handleOpenSubstitutions = useCallback(() => setSubstitutionsDialogOpen(true), []);
  // ANIMATE IN (after the subs are logged) closes the dialog and opens Maestro on the
  // Substitution caption, where the operator animates the just-logged substitution.
  // Copilot has an imperative Maestro invoker; drive mode's DriveMaestro doesn't expose
  // any external "open this caption" API — it only reacts to real clicks on its own
  // rendered buttons. So click the actual "Clock Subs" button it renders, same pattern
  // as the card-logging flow in LineupPanel.jsx; the F3 dispatch is only a last-resort
  // fallback if that button isn't found (e.g. not rendered yet).
  const handleAnimateSubstitution = useCallback(() => {
    setSubstitutionsDialogOpen(false);
    if (isCopilot) {
      copilotTabControl?.invokers?.Maestro?.({
        graphic: getGraphicForCaption("Substitution"),
        initialCaption: "Substitution",
      });
    } else {
      setTimeout(() => {
        const buttons = Array.from(document.querySelectorAll('button'));
        const targetBtn = buttons.find((b) =>
          b.textContent.includes('Clock Subs') ||
          (b.textContent.includes('Sub') && b.textContent.includes('F3')),
        );
        if (targetBtn) { targetBtn.click(); return; }
        document.body.dispatchEvent(new KeyboardEvent('keydown', {
          key: 'F3', code: 'F3', keyCode: 114, which: 114, bubbles: true,
        }));
      }, 50);
    }
  }, [isCopilot, copilotTabControl.invokers]);
  // Clicking a card square (or logging a card via the name-click dialog) opens Maestro on
  // the "Cards" caption — NOT the caption configured on the copilot shortcuts.
  const handleOpenCardsMaestro = useCallback(() => {
    copilotTabControl?.invokers?.Maestro?.({
      graphic: getGraphicForCaption("Cards"),
      initialCaption: "Cards",
    });
  }, [copilotTabControl.invokers]);
  // Clicking a stat tile opens the Maestro component for that stat's caption
  // (operator animates it there) instead of animating headlessly — mirrors the
  // Cards flow above.
  const handleStatTileOpenMaestro = useCallback((stat) => {
    const caption = stat?.name || stat?.key || STAT_CAPTION[stat?.key] || "MatchStats";
    copilotTabControl?.invokers?.Maestro?.({
      graphic: getGraphicForCaption(caption),
      initialCaption: caption,
      displayName: statTileTitle(stat),
      displaySubtitle: statTileSubtitle(stat),
    });
  }, [copilotTabControl.invokers]);
  // "Clear Output": animate out everything on screen except the base PermScore,
  // from the broker's live per-layer state, on any tab. Never touches lineup.
  const handleClearOutput = useCallback(async () => {
    const res = await clearOutput(actionState);
    if (res?.cleared) {
      showNotification(`Output cleared — ${res.outs.map((o) => `${o.action} on ${o.layer}`).join(", ")}`, "success");
    } else if (res?.reason === "nothing-active") {
      showNotification("Nothing on air to clear", "info");
    } else {
      showNotification("Could not clear output", "error");
    }
  }, [clearOutput, actionState, showNotification]);
  const copilotPanelComponents = useMemo(
    () => ({ ...copilotTabControl.invokers, openSubstitutions: handleOpenSubstitutions }),
    [copilotTabControl.invokers, handleOpenSubstitutions],
  );


  // Write a fixture_result score straight to DDS (+ optimistic local update).
  // Reused for the immediate "+"-click score bump. On-air animation is left to
  // the goal-logging confirm; here we just get the number into DDS + copilot UI.
  const persistScoreToDDS = useCallback(async (newScore) => {
    updateFixtureState?.({
      collection: "fixture_result",
      data: {
        home_team_score: newScore.home_team_score,
        away_team_score: newScore.away_team_score,
      },
    });
    const session = getStoredSession();
    const originSite = session?.username || session?.user_name || "aedrive";
    const addedBy = session?.username || "aedrive";
    for (const { path, value, suffix } of [
      { path: "data.home_team_score", value: newScore.home_team_score, suffix: "score_add_home" },
      { path: "data.away_team_score", value: newScore.away_team_score, suffix: "score_add_away" },
    ]) {
      const patchId = `P${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${suffix}`;
      const payload = sendPatchesFromDrivePreset({
        collection: "fixture_result",
        originSite,
        op: "PATCH",
        valid_from: new Date().toISOString(),
        added_by: addedBy,
        patch_id: patchId,
        path,
        value,
        extraKey: { fixture_id: selectedFixtureId, competition_id: competitionId, season_id: seasonId },
      });
      try {
        await dds.http.request({ query: payload.query, variables: payload.variables });
      } catch (e) {
        console.error("[addGoal] fixture_result PATCH failed", path, e);
      }
    }
  }, [updateFixtureState, selectedFixtureId, competitionId, seasonId]);

  // Full time per this screen's period OR the saved clock_end_2h event — the
  // period alone resets to PRE-GAME on reload / on another operator's screen.
  const matchEnded = period === "FULL TIME" || isMatchEnded(fixtureEvents);

  const handleCopilotAddGoalClick = useCallback((teamSide) => {
    // Rule: once the match is finished (second half ended → "FULL TIME"),
    // goals can no longer be logged in copilot.
    if (matchEnded) {
      showNotification("Match ended — goals can't be logged after full time.", "warning");
      return;
    }
    // Fresh attempt: not yet confirmed. If the operator cancels the dialog, the
    // close handler rolls the optimistic +1 back out of DDS (see below).
    goalConfirmedRef.current = false;
    // 1) Capture the exact second the '+' was hit — this is the goal's Time
    //    Awarded (accurate to the second, not rounded to the minute).
    setGoalClickSeconds(parseClockToSeconds(clockTime));
    // Remember the pre-goal score so the confirm's goal animation still sees a
    // +1 delta even though DDS is bumped now.
    setGoalPreScore({
      home_team_score: result?.home_team_score || 0,
      away_team_score: result?.away_team_score || 0,
    });
    // 2) Update the score in DDS immediately (clicked side +1). The goal EVENT
    //    is logged later, when the operator finishes the logging dialog. Own-goal
    //    (if chosen) re-credits the other side at confirm; the confirm patches the
    //    final event-derived score (idempotent for a normal goal).
    const newScore = {
      home_team_score: (result?.home_team_score || 0) + (teamSide === "home" ? 1 : 0),
      away_team_score: (result?.away_team_score || 0) + (teamSide === "away" ? 1 : 0),
    };
    persistScoreToDDS(newScore);

    triggerScoreUpdate(teamSide, newScore, homeTeam, awayTeam);
    // 3) Open the logging dialog (modal → blocks another '+' until done).
    setCopilotGoalTeam(teamSide);
    setCopilotGoalDialogOpen(true);
  }, [matchEnded, showNotification, clockTime, result, persistScoreToDDS, triggerScoreUpdate, homeTeam, awayTeam]);

  const handleGoalDialogClose = useCallback(() => {
    setCopilotGoalDialogOpen(false);
    if (goalConfirmedRef.current) return;
    if (goalPreScore) {
      persistScoreToDDS(goalPreScore);
      cancelPendingScoreFinalise(copilotGoalTeam);
      const reds = computeRedCardTabs(fixtureEvents || [], homeTeam?.team_id, awayTeam?.team_id);
      sendApplyDataState?.({
        scores: {
          home: goalPreScore.home_team_score ?? 0,
          away: goalPreScore.away_team_score ?? 0,
        },
        homeTeam,
        awayTeam,
        redCardTabs: reds,
      }).catch((e) => {
        console.error("[handleGoalDialogClose] cancel-goal APPLY_DATA revert failed", e);
      });
      showNotification("Goal cancelled — score reverted", "info");
    }
  }, [goalPreScore, persistScoreToDDS, showNotification, cancelPendingScoreFinalise, sendApplyDataState, fixtureEvents, homeTeam, awayTeam, copilotGoalTeam]);

  // Clicking a team's score tile opens the EVENTS timeline with THAT team's
  // most recent goal in the edit window (OPUS-1219 / OPUS-1635), where the
  // operator can change scorer, goal type or time. If the team has no goal yet,
  // just open the timeline. The "−" button only removes (confirm lives in
  // StatsPanel).
  const handleCopilotEditGoalClick = useCallback((teamSide) => {
    // Rule: once the match is finished ("FULL TIME") the score is locked.
    if (matchEnded) {
      showNotification("Match ended — goals can't be changed after full time.", "warning");
      return;
    }
    const teamId = teamSide === "home" ? homeTeam?.team_id : awayTeam?.team_id;
    const latestGoal = findLatestTeamGoal(fixtureEvents, teamId);
    if (latestGoal?.event_id) setReassignGoalEventId(latestGoal.event_id);
    setCopilotEventsOpen(true);
  }, [matchEnded, showNotification, fixtureEvents, homeTeam, awayTeam]);

  // OPUS-1615 — raw Opta events come only from an opt-in DDS query. The graphic
  // says which captions need them, and which fields, in v_captions["x-dataOptIns"];
  // both caption windows report the open caption's entry through onCaptionChange.
  // Fetch while that caption is on screen and lay them on a copy of the match data
  // below. getContext stays a cheap merge: the window calls it on every DDS patch
  // and every send.
  const [openCaption, setOpenCaption] = useState(null);
  const optaOptIn = openCaption?.dataOptIns?.optaEvents;
  const optaEvents = useOptaEvents({
    wanted: Boolean(optaOptIn),
    fields: optaOptIn?.fields,
    competitionId,
    seasonId,
    fixtureId: selectedFixtureId,
  });

  // Preset Modal Services
  const dataService = useMemo(
    () => ({
      getContext: async () => withOptaEvents(fixtureState, optaEvents),
      evaluate: async (expr, context, options) => {
        try {
          if (typeof expr === "function")
            return expr(context?.data || context, options);
          if (typeof expr === "string" && expr.includes("=>")) {
            const fn = new Function("data", "options", `return ${expr}`);
            return fn(context?.data || context, options);
          }
          return expr;
        } catch (e) {
          return expr;
        }
      },
    }),
    [fixtureState, optaEvents],
  );

  // Populate the recording context consumed by useBrokerService.sendMutation
  // (the generic Maestro-action recorder). Assigned every render once clock /
  // fixture / period are resolved; read lazily at send time.
  matchContextRef.current = {
    session:
      fixture?.fixture_session ??
      (period === "SECOND HALF" ? 2 : period === "FIRST HALF" ? 1 : null),
    clockTime,
    fixtureId: selectedFixtureId,
    competitionId,
    seasonId,
    profileId,
    userRole,
    fixtureEvents,
    // Event-service entry point for raw Maestro caption actions (the service
    // decides which to process). Read lazily by handleMaestroAction above.
    handleCaptionAction: eventService?.handleCaptionAction,
  };

  const brokerServiceObj = useMemo(
    () => ({
      // TXLive only generates the payload and forwards it — recording of
      // allow-listed actions happens inside useBrokerService.sendMutation.
      send: async (cmd) => {
        if (cmd) {
          const payload = {
            // Honour an explicit per-command renderer (PREVIEW-mode
            // commands carry a retargeted preview renderer); fall back
            // to the program renderer for everything else.
            renderer: cmd.renderer || getRendererIdToUse(),
            layer: cmd.caption.id,
            caption: {
              id: cmd.caption.id,
              action: cmd.caption.action,
              ...(cmd.caption.mode ? { mode: cmd.caption.mode } : {}),
              data: cmd.caption.data,
            },
          };

          // brokerSendMutation (the choke-point) silently suppresses this when the
          // renderer is locked — no network call reaches the broker. Skip the
          // "sent" message in that case so we don't claim a command was sent when it
          // wasn't; stay silent otherwise (no lock-specific message here).
          const result = await brokerSendMutation(stripScoreFromOverlayTake(payload));
          if (!result?.suppressed) {
            showNotification(
              `Command ${payload.caption.action}${payload.caption.mode ? ` (${payload.caption.mode})` : ""} sent for ${payload.layer}`,
              "success",
            );
          }
        }
      },
      getState: () => {
        return actionState;
      },
    }),
    [getRendererIdToUse, updateRenderer],
  );

  // --- MEMOIZED HANDLERS ---



  // format formation
  const formatFormation = (formation) =>
    formation ? String(formation).split("").join("-") : "";


  // --- DYNAMIC COLORS ---
  const getTeamColor = (team, isHome) => {
    if (!team) return isHome ? "#45FFD5" : "#D1030D";

    // Default to 'H' (Home Kit) if not found
    let kitType = "H";
    try {
      const settings = team.fixture_team_setting || [];
      if (settings.length > 0 && settings[0].kit_type) {
        const parts = settings[0].kit_type.split("_");
        if (parts.length >= 2) {
          kitType = parts[1]; // 0-indexed 1 is the 2nd string
        }
      }
    } catch (e) {
      console.warn("Error parsing kit type", e);
    }

    // Map the kit suffix to its colour field. Standard suffixes use the slot
    // word (home/away/third); any custom suffix (e.g. "CEL" from "NEW_CEL") uses
    // its own lower-cased prefix → cel_shirt_colour_1. Mirrors the Admin Panel's
    // colourPrefixFromAsset so a custom kit's colour actually renders.
    const prefix = isHome ? "home" : "away";
    let colorKey;
    if (kitType === "A") colorKey = `away_shirt_colour_1`;
    else if (kitType === "T") colorKey = `third_shirt_colour_1`;
    else if (kitType === "H") colorKey = `home_shirt_colour_1`;
    else colorKey = `${kitType.toLowerCase()}_shirt_colour_1`; // custom kit

    // Safely retrieve
    let color = team[colorKey];

    // Fallback if specific kit color is missing
    if (!color) color = team[`${prefix}_shirt_colour_1`];

    return color || (isHome ? "#45FFD5" : "#D1030D");
  };

  const homeAccentColor = useMemo(
    () => getTeamColor(homeTeam, true),
    [homeTeam],
  );
  const awayAccentColor = useMemo(
    () => getTeamColor(awayTeam, false),
    [awayTeam],
  );

  const homeTextColor = useMemo(
    () => getContrastYIQ(homeAccentColor),
    [homeAccentColor],
  );
  const awayTextColor = useMemo(
    () => getContrastYIQ(awayAccentColor),
    [awayAccentColor],
  );

  const copilotBottomRows = useMemo(() => {
    const base = COPILOT_LAYOUT_PRESETS.balanced;
    return Math.max(...base.map((i) => i.y + i.h), 1);
  }, []);
  const [copilotRowHeight, setCopilotRowHeight] = useState(100);
  const [copilotScale, setCopilotScale] = useState(1);
  // useLayoutEffect (not useEffect) so the row height is set before the first
  // paint — otherwise the grid paints once at the 100px default and then
  // jumps to the computed height, a visible vertical flash on load.
  useLayoutEffect(() => {
    if (!isCopilot) return undefined;
    const FOOTER_RESERVE = 62; // the footer bar's height; the grid fills down to it
    const rowFor = (h) =>
      (h - FOOTER_RESERVE - 2 * COPILOT_EDGE - (copilotBottomRows - 1) * COPILOT_GAP) / copilotBottomRows;
    const baseRow = rowFor(1080); // row height the layout was tuned at
    const compute = () => {
      // Lay out on the 1920x1080 canvas and zoom the page to fit the window,
      // so a smaller window shows the same screen scaled down, not reflowed.
      const canvas = fitCanvas(window.innerWidth, window.innerHeight);
      document.documentElement.style.zoom = canvas.zoom < 1 ? String(canvas.zoom) : "";
      const rh = rowFor(canvas.height);
      setCopilotRowHeight(Math.max(56, Math.floor(rh)));
      // Clamp to 1 (≥1080 stays pixel-identical); floor at 0.78.
      setCopilotScale(Math.min(1, Math.max(0.78, rh / baseRow)));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("resize", compute);
      document.documentElement.style.zoom = "";
    };
  }, [isCopilot, copilotBottomRows]);

  // Measure the grid container ourselves (see the WidthProvider note up top).
  // A ref callback fires during commit — before the browser paints — so the
  // setGridWidth re-render lands in the same frame and <Responsive> renders
  // at its final width on the very first paint (no 1280→actual slide).
  const [gridWidth, setGridWidth] = useState(0);
  const gridResizeObsRef = useRef(null);
  const measureGridWidth = useCallback((node) => {
    if (gridResizeObsRef.current) {
      gridResizeObsRef.current.disconnect();
      gridResizeObsRef.current = null;
    }
    if (!node) return;
    setGridWidth(node.offsetWidth);
    if (typeof ResizeObserver !== "undefined") {
      const obs = new ResizeObserver(() => setGridWidth(node.offsetWidth));
      obs.observe(node);
      gridResizeObsRef.current = obs;
    }
  }, []);

  // Copilot spatial keyboard navigation (arrow keys jump focus across sections).
  useCopilotSpatialNav(isCopilot);

  useCopilotButtonShortcuts(
    copilotTabControl.panelConfig,
    copilotPanelComponents,
    isCopilot && copilotTabControl.ready && !copilotTabControl.maestroOpen,
  );

  // Show loader while fixture or renderer are actively resolving.
  // Do not block forever on missing/deleted renderer details.
  if (
    hasFixtureSelected &&
    (loading || resolvingRenderer || validatingPresets)
  ) {
    return (
      <Loader
        message={
          validatingPresets
            ? "Validating captions..."
            : resolvingRenderer
              ? "Loading renderer details..."
              : "Loading fixture data..."
        }
      />
    );
  }
  // Badge folder comes from the SELECTED RENDERER'S SCENE when that scene
  // defines one (load_team_badge_path), so the crests match the artwork the
  // graphics are actually rendered from. Falls back to the env bucket, then to
  // the legacy assets. Shared with TXLivePage so the two cannot drift.
  const homeTeamLogo = resolveTeamLogo(homeTeam, {
    renderer: selectedRenderer,
    fallback: ManchsterLogo,
  });
  const awayTeamLogo = resolveTeamLogo(awayTeam, {
    renderer: selectedRenderer,
    fallback: GrimsbyTownFCLogo,
  });
  // Formation is now a persisted team setting (parallel to kit_type, read at line
  // ~1340). Prefer it so Drive matches Aether after a lineup clear/reset instead
  // of showing the stale lineup-row formation; fall back to the row for legacy.
  const homeTeamFormation = formatFormation(homeTeam?.fixture_team_setting?.[0]?.formation ?? homeTeam?.lineup?.[0]?.formation);
  const awayTeamFormation = formatFormation(awayTeam?.fixture_team_setting?.[0]?.formation ?? awayTeam?.lineup?.[0]?.formation);
  const homeTeamShortName = homeTeam?.team_short_name;
  const awayTeamShortName = awayTeam?.team_short_name;

  // Copilot grid uses a single fixed preset (see config/copilotLayouts.js) and
  // the tiles are always locked (static) — the layout isn't operator-editable.
  const copilotLayout = COPILOT_LAYOUT_PRESETS.balanced.map((item) => ({
    ...item,
    static: isCopilot,
  }));

  const homeLineupElement = (
    <Box
      ref={panelRefs.homeLineup}
      data-player-list="home"
      sx={{ outline: "none", height: "100%" }}
    >
      {hasFixtureSelected ? (
        <LineupPanel
          teamName={homeTeam?.team_short_name || homeTeam?.team_name}
          teamTricode={homeTeam?.team_tricode}
          players={homeLineup.players}
          subs={homeLineup.subs}
          staff={homeLineup.staff}
          accentColor={homeAccentColor}
          logoUrl={homeTeamLogo}
          teamColor={homeAccentColor}
          textColor={homeTextColor}
          competitionId={competitionId}
          teamId={homeTeam?.team_id}
          fixtureId={selectedFixtureId}
          seasonId={seasonId}
          playersListRef={homePlayersListRef}
          activeSection={activeLineup}
          side="home"
          onInteract={(section) => setActiveLineup(section)}
          formation={homeTeamFormation}
          team_short_name={homeTeamShortName}
          isCopilot={isCopilot}
          fixture={fixture}
          clockTime={clockTime}
          profileId={profileId}
          userRole={userRole}
          eventService={eventService}
          getRendererIdToUse={getRendererIdToUse}
          homeTeamId={homeTeam?.team_id}
          awayTeamId={awayTeam?.team_id}
          onNotify={showNotification}
          fixtureEvents={fixtureEvents}
          onCardIconClick={isCopilot ? handleOpenCardsMaestro : undefined}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          updateFixtureState={updateFixtureState}
          updateRenderer={updateRenderer}
          rendererId={selectedRenderer?.renderer_id || "OP-BLUEBIRD-TEST929"}
        />
      ) : (
        <Box
          sx={{
            bgcolor: "#1E1E1E",
            borderRadius: 2,
            p: 2,
            height: "100%",
            minHeight: 400,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.9rem",
              textAlign: "center",
              mt: 4,
            }}
          >
            Home Team
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.3)",
              fontSize: "0.75rem",
              textAlign: "center",
              mt: 1,
            }}
          >
            Select a fixture to view lineup
          </Typography>
        </Box>
      )}
    </Box>
  );

  const awayLineupElement = (
    <Box
      ref={panelRefs.awayLineup}
      data-player-list="away"
      sx={{ outline: "none", height: "100%" }}
    >
      {hasFixtureSelected ? (
        <LineupPanel
          teamName={awayTeam?.team_short_name || awayTeam?.team_name}
          teamTricode={awayTeam?.team_tricode}
          players={awayLineup.players}
          subs={awayLineup.subs}
          staff={awayLineup.staff}
          accentColor={awayAccentColor}
          logoUrl={awayTeamLogo}
          teamColor={awayAccentColor}
          textColor={awayTextColor}
          competitionId={competitionId}
          teamId={awayTeam?.team_id}
          fixtureId={selectedFixtureId}
          seasonId={seasonId}
          playersListRef={awayPlayersListRef}
          activeSection={activeLineup}
          side="away"
          onInteract={(section) => setActiveLineup(section)}
          formation={awayTeamFormation}
          team_short_name={awayTeamShortName}
          isCopilot={isCopilot}
          fixture={fixture}
          clockTime={clockTime}
          profileId={profileId}
          userRole={userRole}
          eventService={eventService}
          getRendererIdToUse={getRendererIdToUse}
          homeTeamId={homeTeam?.team_id}
          awayTeamId={awayTeam?.team_id}
          onNotify={showNotification}
          fixtureEvents={fixtureEvents}
          onCardIconClick={isCopilot ? handleOpenCardsMaestro : undefined}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          updateFixtureState={updateFixtureState}
          updateRenderer={updateRenderer}
          rendererId={selectedRenderer?.renderer_id || "OP-BLUEBIRD-TEST929"}
        />
      ) : (
        <Box
          sx={{
            bgcolor: "#1E1E1E",
            borderRadius: 2,
            p: 2,
            height: "100%",
            minHeight: 400,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.9rem",
              textAlign: "center",
              mt: 4,
            }}
          >
            Away Team
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.3)",
              fontSize: "0.75rem",
              textAlign: "center",
              mt: 1,
            }}
          >
            Select a fixture to view lineup
          </Typography>
        </Box>
      )}
    </Box>
  );

  const clockElement = (
    <Box sx={{ height: "100%" }}>
      {hasFixtureSelected ? (
        <StatsPanels
          score={scoreData}
          stats={teamStats}
          homeAccent={homeAccentColor}
          awayAccent={awayAccentColor}
          rendererId={selectedRenderer?.renderer_id || "OP-BLUEBIRD-TEST929"}
          clockName="MatchClock"
          statsViewMode={statsViewMode}
          onStatsViewModeChange={setStatsViewMode}
          eventLog={eventLog}
          team_short_name={awayTeamShortName}
          isCopilot={isCopilot}
          onAddGoalClick={handleCopilotAddGoalClick}
          onEditGoalClick={handleCopilotEditGoalClick}
          onOpenSubstitutionsDialog={handleOpenSubstitutions}
          fixtureId={selectedFixtureId}
          competitionId={competitionId}
          seasonId={seasonId}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          fixture={fixture}
          competitionName={fixtureState?.competition?.[0]?.competition_name ?? ""}
          fixtureName={fixtureName}
          result={result}
          profileId={profileId}
          userRole={userRole}
          eventService={eventService}
          triggerScoreUpdate={triggerScoreUpdate}
          updateFixtureState={updateFixtureState}
          homeLineup={homeLineup}
          awayLineup={awayLineup}
          fixtureEvents={fixtureEvents}
          clockProfile={clockProfile}
          clockTime={clockTime}
          period={period}
          setPeriod={setPeriod}
          triggerClockAction={triggerClockAction}
          updateRenderer={updateRenderer}
          getRendererIdToUse={getRendererIdToUse}
          publishGoalEvent={publishGoalEvent}
          sendApplyDataState={sendApplyDataState}
          cancelPendingScoreFinalise={cancelPendingScoreFinalise}
          onClockOptionSelect={handleStatsPanelClockOptionSelect}
          onUpdateEvent={triggerEventUpdate}
          onNotify={showNotification}
          copilotScale={copilotScale}
          hideStats
        />
      ) : (
        <Box
          sx={{
            bgcolor: "#1E1E1E",
            borderRadius: 2,
            p: 2,
            height: "100%",
            minHeight: 150,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.9rem",
              textAlign: "center",
              mt: 4,
            }}
          >
            Match Clock
          </Typography>
        </Box>
      )}
    </Box>
  );

  const statsElement = (
    <Box sx={{ height: "100%", width: "100%", minWidth: 0 }}>
      {hasFixtureSelected ? (
        <StatsPanels
          score={scoreData}
          stats={teamStats}
          homeAccent={homeAccentColor}
          awayAccent={awayAccentColor}
          rendererId={selectedRenderer?.renderer_id || "OP-BLUEBIRD-TEST929"}
          clockName="MatchClock"
          statsViewMode={statsViewMode}
          onStatsViewModeChange={setStatsViewMode}
          eventLog={eventLog}
          team_short_name={awayTeamShortName}
          isCopilot={isCopilot}
          fixtureId={selectedFixtureId}
          competitionId={competitionId}
          seasonId={seasonId}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          fixture={fixture}
          competitionName={fixtureState?.competition?.[0]?.competition_name ?? ""}
          fixtureName={fixtureName}
          result={result}
          profileId={profileId}
          userRole={userRole}
          eventService={eventService}
          triggerScoreUpdate={triggerScoreUpdate}
          updateFixtureState={updateFixtureState}
          homeLineup={homeLineup}
          awayLineup={awayLineup}
          fixtureEvents={fixtureEvents}
          clockProfile={clockProfile}
          clockTime={clockTime}
          period={period}
          setPeriod={setPeriod}
          triggerClockAction={triggerClockAction}
          updateRenderer={updateRenderer}
          getRendererIdToUse={getRendererIdToUse}
          publishGoalEvent={publishGoalEvent}
          sendApplyDataState={sendApplyDataState}
          cancelPendingScoreFinalise={cancelPendingScoreFinalise}
          onClockOptionSelect={handleStatsPanelClockOptionSelect}
          onUpdateEvent={triggerEventUpdate}
          onNotify={showNotification}
          eventsModalOpen={copilotEventsOpen}
          onCloseEventsModal={() => { setCopilotEventsOpen(false); setReassignGoalEventId(null); }}
          autoEditEventId={reassignGoalEventId}
          onAutoEditHandled={() => setReassignGoalEventId(null)}
          singleStatTiles={matchStatsTiles.single}
          doubleStatTiles={matchStatsTiles.double}
          advancedStatTiles={matchStatsTiles.advanced}
          activeStatCaption={statInCaption}
          statsGraphicOnAir={onAirStats.matchStatsOnAir}
          onStatClick={handleStatTileOpenMaestro}
          onClearStatsGraphic={handleClearStatsGraphic}
          copilotScale={copilotScale}
          hideClock
        />
      ) : (
        <Box
          sx={{
            bgcolor: "#1E1E1E",
            borderRadius: 2,
            p: 2,
            height: "100%",
            minHeight: 400,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.9rem",
              textAlign: "center",
              mt: 4,
            }}
          >
            Match Stats
          </Typography>
        </Box>
      )}
    </Box>
  );

  const fullStatsOriginalElement = (
    <Box>
      {hasFixtureSelected ? (
        <StatsPanels
          score={scoreData}
          stats={teamStats}
          homeAccent={homeAccentColor}
          awayAccent={awayAccentColor}
          rendererId={selectedRenderer?.renderer_id || "OP-BLUEBIRD-TEST929"}
          clockName="MatchClock"
          statsViewMode={statsViewMode}
          onStatsViewModeChange={setStatsViewMode}
          eventLog={eventLog}
          team_short_name={awayTeamShortName}
          isCopilot={isCopilot}
          onAddGoalClick={handleCopilotAddGoalClick}
          onEditGoalClick={handleCopilotEditGoalClick}
          onOpenSubstitutionsDialog={handleOpenSubstitutions}
          fixtureId={selectedFixtureId}
          competitionId={competitionId}
          seasonId={seasonId}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          fixture={fixture}
          competitionName={fixtureState?.competition?.[0]?.competition_name ?? ""}
          fixtureName={fixtureName}
          result={result}
          profileId={profileId}
          userRole={userRole}
          eventService={eventService}
          triggerScoreUpdate={triggerScoreUpdate}
          updateFixtureState={updateFixtureState}
          homeLineup={homeLineup}
          awayLineup={awayLineup}
          fixtureEvents={fixtureEvents}
          clockProfile={clockProfile}
          clockTime={clockTime}
          period={period}
          setPeriod={setPeriod}
          triggerClockAction={triggerClockAction}
          updateRenderer={updateRenderer}
          getRendererIdToUse={getRendererIdToUse}
          publishGoalEvent={publishGoalEvent}
          sendApplyDataState={sendApplyDataState}
          cancelPendingScoreFinalise={cancelPendingScoreFinalise}
          onClockOptionSelect={handleStatsPanelClockOptionSelect}
          onUpdateEvent={triggerEventUpdate}
          onNotify={showNotification}
        />
      ) : (
        <Box
          sx={{
            bgcolor: "#1E1E1E",
            borderRadius: 2,
            p: 2,
            height: "100%",
            minHeight: 400,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.9rem",
              textAlign: "center",
              mt: 4,
            }}
          >
            Match Stats
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.3)",
              fontSize: "0.75rem",
              textAlign: "center",
              mt: 1,
            }}
          >
            Select a fixture to view stats
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Maestro needs FLAT graphic props (graphicId/path/schema/…) to enter compose
  // mode; spread them from the fetched definition below.
  const copilotMaestroDef = copilotTabControl.activeGraphic?.captionDefinition || {};

  // Inject the copilot caption→widget mapping into v_ui so Maestro renders our
  // custom widgets. A definition's own x-captionWidgets (if present) wins.
  const copilotRendererUrl = selectedRenderer?.renderer_id
    ? `${process.env.REACT_APP_RENDERER_PREVIEW_URL}/?renderer=${encodeURIComponent(selectedRenderer.renderer_id)}`
    : undefined;
  const copilotMaestroUiSchema = {
    ...(copilotMaestroDef.v_ui || {}),
    "x-captionWidgets": {
      ...buildCopilotCaptionWidgets({
        homeAccent: homeAccentColor,
        awayAccent: awayAccentColor,
        rendererUrl: copilotRendererUrl,
        clockConstraints: copilotClockConstraints,
      }),
      ...(copilotMaestroDef.v_ui?.["x-captionWidgets"] || {}),
    },
  };

  // Caption button grid geometry (TabControl's largeButtons gap is 1.25 = 10px).
  const CAPTION_GRID_GAP = 12;
  const CAPTION_GRID_ROWS = 6;
  const captionRowPx = Math.round(88 * copilotScale);
  // 14px puts the first row of buttons level with the top of the team sheets.
  const captionGridPadTop = Math.round(14 * copilotScale);
  const captionGridPadBottom = Math.round(8 * copilotScale);

  const controlsElement = isCopilot ? (
    <Box
      ref={controlsContainerRef}
      data-copilot-tabs="true"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        // Caption tabs lead the type scale. The stats tabs below are a size
        // smaller so they read as a filter, not navigation.
        "& .MuiTab-root": {
          fontSize: Math.round(40 * copilotScale),
          fontFamily: '"Barlow", sans-serif',
          fontWeight: 600,
        },
        "& .MuiTab-root:not(.Mui-selected):hover": { color: "#fff" },
        // MUI places its sliding indicator from screen-pixel measurements, which
        // lands it off the tab under the canvas zoom; underline the tab itself.
        "& .MuiTabs-indicator": { display: "none" },
        "& .MuiTab-root.Mui-selected": { boxShadow: "inset 0 -4px 0 #4fc3f7" },
        // Drop the faint full-width separator under the tab row (keep the blue
        // selected-tab indicator). With headerActions present, the lib puts that
        // 1px border on the flex wrapper AROUND the tabs, so target the parent.
        "& .MuiTabs-root": { borderBottom: "none !important" },
        // The lib centres tabs + headerActions as one group; keep tabs left and push
        // EVENTS / CLEAR OUTPUT to the right edge, inset like DEANIMATE CLOCK STATS below (px: 2).
        "& div:has(> .MuiTabs-root)": { borderBottom: "none !important", justifyContent: "flex-start !important" },
        "& div:has(> .MuiTabs-root) > div:last-child": { marginLeft: "auto", paddingRight: "16px" },
        // TabControl's largeButtons grid has a FIXED gridAutoRows:138px; size rows so
        // three fit above the stats (scaled, 1:1 at 1080). :has() pins the lib's grid.
        "& div:has(> span > [data-action-btn])": {
          gridAutoRows: `${captionRowPx}px !important`,
          gap: `${CAPTION_GRID_GAP}px !important`,
          // The 8px side padding leaves room for the buttons' focus rings; the
          // negative margin lines the buttons up with the column edge.
          mx: "-8px",
          pt: `${captionGridPadTop}px !important`,
          pb: `${captionGridPadBottom}px !important`,
          // Always three rows tall when the stats share the column, so tabs with fewer
          // buttons don't shift the stats; more buttons scroll. flex:none beats the lib's
          // flex:1 (which would size it to its content). The box is border-box, so
          // the height includes the padding.
          ...(copilotTabControl.showStats && {
            flex: "none !important",
            height: `${3 * captionRowPx + 2 * CAPTION_GRID_GAP + captionGridPadTop + captionGridPadBottom}px`,
          }),
          // Without stats the box fills the column and six rows share it, the
          // most rows a definition seats buttons in.
          ...(!copilotTabControl.showStats && {
            gridTemplateRows: `repeat(${CAPTION_GRID_ROWS}, minmax(0, 1fr))`,
            // Past the column by its bottom padding, so the last row ends level
            // with the team sheets.
            mb: `-${captionGridPadBottom}px`,
          }),
        },
        "& [data-action-btn]": {
          minHeight: "0 !important",
          fontSize: `${Math.round(22 * copilotScale)}px !important`,
          fontWeight: "600 !important",
          letterSpacing: "0.04em !important",
        },
      }}
    >

      {copilotTabControl.ready && (
      <TabControl
        defaultTab={0}
        config={copilotTabControl.buttonConfig}
        components={copilotPanelComponents}
        onTabChange={copilotTabControl.onTabChange}
        largeButtons
        headerActions={(
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Soft renderer-lock badge moved to the app footer, beside renderer
                selection (Footer.jsx) — it used to render here and crowded the
                CLOCK/PLAYER/STATS/SP tab row. */}
            <Button
              size="small"
              onClick={() => setCopilotEventsOpen(true)}
              className="no-drag"
              // Same outlined shape as the CLEAR buttons, in the events yellow.
              sx={{
                minWidth: 0,
                height: 48,
                px: 2.25,
                fontSize: Math.round(22 * copilotScale),
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
                borderRadius: 1.5,
                border: "1px solid rgba(254,236,103,0.4)",
                color: "#feec67",
                "&:hover": { color: "#fff", borderColor: "#feec67", bgcolor: "rgba(254,236,103,0.08)" },
              }}
            >
              Events
            </Button>
            {/* Animate out everything on screen except the base PermScore. Same pill
                as Deanimate Clock Stats, kept furthest right; available on every tab. */}
            <Box sx={{ ml: 1.5 }}>
              <ClearButton
                label="Clear Output"
                onClick={handleClearOutput}
                disabled={isRendererLocked}
                title={isRendererLocked ? "Renderer locked — view only" : "Animate out every graphic on screen except the clock"}
              />
            </Box>
          </Box>
        )}
      />
      )}

      {/*
        Mounted while a graphic is selected, not only while the window shows.
        MaestroProvider is built for this — `open` scopes the press state and
        the keyboard shortcuts, and DriveMaestro stays mounted the same way —
        and it keeps the preview renderer inside the developer dock warm across
        a close and reopen instead of reloading it every time.
      */}
      {copilotTabControl.activeGraphic && (
        <CopilotMaestroChrome
          key={copilotTabControl.activeGraphic.id}
          developerOpen={copilotDeveloper.open}
          open={copilotTabControl.maestroOpen}
          onClose={copilotTabControl.closeMaestro}
          onUserAction={handleMaestroAction}
          onCaptionChange={setOpenCaption}
          mount="modal"
          enableExitActions={true}
          graphicId={copilotMaestroDef.id}
          graphicPath={copilotMaestroDef.path}
          graphicName={copilotTabControl.activeGraphic?.displayName || copilotMaestroDef.name}
          graphicSubtitle={copilotTabControl.activeGraphic?.displaySubtitle}
          schema={copilotMaestroDef.schema}
          uiSchema={copilotMaestroUiSchema}
          captionsSchema={copilotMaestroDef.v_captions}
          actionsSchema={copilotMaestroDef.v_actions}
          customActions={copilotMaestroDef.customActions}
          controllersConfig={copilotMaestroDef.v_config}
          rendererId={selectedRenderer?.renderer_id || rendererId}
          surface="live"
          dataService={dataService}
          brokerService={brokerServiceObj}
          bindData={bindData}
          enableImportPanel={true}
          onFormDataChange={noopFormDataChange}
          initialCaption={copilotTabControl.activeCaption}
          customInputWidgets={{
            substitutionPanel: SubstitutionPanel,
            additionalTimePanel: AdditionalTimePanel,
            CopilotClockResetWidget,
            CopilotClockOverwriteWidget,
            CopilotMatchStatsWidget,
            CopilotSubstitutionWidget,
          }}
          additionalWidgets={{ clockControl: ClockControl }}
          libraryVersions={{ ui: MAESTRO_UI_VERSION }}
          defaultUiSettings={MAESTRO_DEFAULT_UI_SETTINGS}
        />
      )}
    </Box>
  ) : (
    <Box
      sx={{
        width: "100%",
        maxWidth: 900,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
        height: "100%",
      }}
    >
      {/* Renderer overlay */}
      <Fade
        in={rendererOverlayOpen}
        timeout={{ enter: 300, exit: 250 }}
        unmountOnExit
      >
        <Box
          onClick={onCloseRendererOverlay}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "30%",
            bgcolor: "rgba(0,0,0,0.85)",
            zIndex: 10,
            borderRadius: 2,
          }}
        />
      </Fade>
      <Slide
        in={rendererOverlayOpen}
        direction="down"
        timeout={{ enter: 350, exit: 300 }}
        unmountOnExit
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "30%",
            zIndex: 11,
            pointerEvents: "auto",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {rendererOverlayContent}
        </Box>
      </Slide>
      {/* Preset area */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        {hasRendererSelected ? (
          <Box sx={{ bgcolor: "#1E1E1E", borderRadius: 2, p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: "1rem" }}
                >
                  {selectedRenderer?.renderer_id || "No Renderer"}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "0.75rem",
                    mt: 0.5,
                  }}
                >
                  {selectedRenderer?.scene_path
                    ?.split("/")
                    .pop()
                    .toUpperCase() || "No scene"}
                </Typography>
                {/* Soft renderer-lock badge now lives in the footer, beside
                    renderer selection (Footer.jsx) — kept in one place instead
                    of duplicating it here too. */}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setShortcutRegistryOpen(true)}
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    "&:hover": {
                      bgcolor: "rgba(254, 236, 103, 0.15)",
                      color: "#FEEC67",
                    },
                  }}
                  title="Shortcut registry"
                >
                  <KeyboardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!selectedRenderer?.renderer_id}
                  onClick={() => {
                    if (!selectedRenderer?.renderer_id) return;
                    const url = `${process.env.REACT_APP_RENDERER_PREVIEW_URL}/?renderer=${encodeURIComponent(selectedRenderer.renderer_id)}`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#fff",
                    },
                  }}
                  title="Open preview in new tab"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={
                    !selectedRenderer?.renderer_id ||
                    !window.documentPictureInPicture
                  }
                  onClick={async () => {
                    if (!selectedRenderer?.renderer_id) return;
                    if (!window.documentPictureInPicture) {
                      showNotification(
                        "Picture-in-Picture not supported in this browser",
                        "error",
                      );
                      return;
                    }
                    try {
                      const url = `${process.env.REACT_APP_RENDERER_PREVIEW_URL}/?renderer=${encodeURIComponent(selectedRenderer.renderer_id)}`;
                      const previewOrigin = new URL(url).origin;
                      const pipWin =
                        await window.documentPictureInPicture.requestWindow({
                          width: 640,
                          height: 360,
                        });
                      const d = pipWin.document;
                      d.title = selectedRenderer.renderer_id;
                      d.body.style.cssText =
                        "margin:0;background:#000;font-family:sans-serif;color:#fff;";

                      const style = d.createElement("style");
                      style.textContent = `
                        @keyframes pipFade { 0%,100% { opacity: 0.35; } 50% { opacity: 1; } }
                      `;
                      d.head.appendChild(style);

                      const progressWrap = d.createElement("div");
                      progressWrap.style.cssText =
                        "position:fixed;inset:0;z-index:10;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(0,0,0,0.7);backdrop-filter:blur(6px);transition:opacity 0.4s;";

                      const iconHolder = d.createElement("div");
                      const iconInner = d.createElement("img");
                      iconInner.src = `${window.location.origin}/AeroFavicon.ico`;
                      iconInner.alt = "Aero";
                      iconInner.style.cssText =
                        "width:72px;height:72px;animation: pipFade 1.4s ease-in-out infinite;display:block;";
                      iconHolder.appendChild(iconInner);

                      progressWrap.appendChild(iconHolder);
                      d.body.appendChild(progressWrap);

                      const iframe = d.createElement("iframe");
                      iframe.src = url;
                      iframe.style.cssText =
                        "border:0;width:100vw;height:100vh;display:block;";
                      iframe.allow = "autoplay";
                      d.body.appendChild(iframe);

                      const openedAt = Date.now();
                      const MIN_SHOW_MS = 1200;
                      let finished = false;
                      const finish = () => {
                        if (finished) return;
                        finished = true;
                        const remaining = Math.max(
                          0,
                          MIN_SHOW_MS - (Date.now() - openedAt),
                        );
                        setTimeout(() => {
                          progressWrap.style.opacity = "0";
                          setTimeout(() => {
                            progressWrap.remove();
                          }, 500);
                        }, remaining);
                      };

                      const onMsg = (e) => {
                        if (e.origin !== previewOrigin) return;
                        const data = e.data;
                        if (!data || typeof data !== "object") return;
                        if (
                          data.type === "loadProgress" &&
                          typeof data.loaded === "number" &&
                          typeof data.total === "number" &&
                          data.total > 0
                        ) {
                          if (data.loaded >= data.total) finish();
                        } else if (data.type === "loadComplete") {
                          finish();
                        }
                      };
                      pipWin.addEventListener("message", onMsg);
                      iframe.addEventListener("load", finish);
                      pipWin.addEventListener("pagehide", () =>
                        pipWin.removeEventListener("message", onMsg),
                      );
                    } catch (err) {
                      showNotification(
                        "Failed to open Picture-in-Picture",
                        "error",
                      );
                    }
                  }}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#fff",
                    },
                  }}
                  title={
                    window.documentPictureInPicture
                      ? "Open preview in Picture-in-Picture"
                      : "PiP not supported in this browser"
                  }
                >
                  <PictureInPictureAltIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!selectedRenderer?.renderer_id}
                  onClick={async () => {
                    if (!selectedRenderer?.renderer_id) return;
                    const url = `${process.env.REACT_APP_RENDERER_PREVIEW_URL}/?renderer=${encodeURIComponent(selectedRenderer.renderer_id)}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      showNotification("Preview URL copied", "success");
                    } catch {
                      showNotification("Failed to copy URL", "error");
                    }
                  }}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#fff",
                    },
                  }}
                  title="Copy renderer URL"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box
              ref={panelRefs.presets}
              tabIndex={0}
              onFocus={() => setFocusedElement("presets")}
              sx={{
                outline: "none",
                borderRadius: 2,
              }}
            >
              <DriveMaestro
                // Remount on a renderer switch — Maestro only reads its
                // persisted settings (programRendererUrl) when it mounts.
                key={selectedRenderer?.renderer_id || rendererId}
                enableExitActions={true}
                driveCaptions={driveCaptions}
                drivePresetValidity={presetValidity}
                onDriveInvalid={handleDriveInvalid}
                rendererId={selectedRenderer?.renderer_id || rendererId}
                surface="live"
                dataService={dataService}
                brokerService={brokerServiceObj}
                bindData={bindData}
                enableImportPanel={true}
                onFormDataChange={noopFormDataChange}
                onUserAction={handleMaestroAction}
                onCaptionChange={setOpenCaption}
                ButtonComponent={CaptionButton}
                customInputWidgets={{
                  substitutionPanel: SubstitutionPanel,
                  additionalTimePanel: AdditionalTimePanel,
                  CopilotClockResetWidget,
                  CopilotClockOverwriteWidget,
                  CopilotMatchStatsWidget,
                  CopilotSubstitutionWidget,
                }}
                additionalFields={MAESTRO_ADDITIONAL_FIELDS}
                additionalWidgets={{ clockControl: ClockControl }}
                libraryVersions={{ ui: MAESTRO_UI_VERSION }}
                defaultUiSettings={MAESTRO_DEFAULT_UI_SETTINGS}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ bgcolor: "#1E1E1E", borderRadius: 2, p: 2, minHeight: 120 }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.9rem",
                textAlign: "center",
                mt: 2,
              }}
            >
              Presets
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.3)",
                fontSize: "0.75rem",
                textAlign: "center",
                mt: 1,
              }}
            >
              Select a renderer to view presets
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  const sharedOverlays = (
    <>
      <Fade
        in={fixtureOverlayOpen}
        timeout={{ enter: 300, exit: 250 }}
        unmountOnExit
      >
        <Box
          onClick={onCloseFixtureOverlay}
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.85)",
            zIndex: 10,
          }}
        />
      </Fade>
      <Slide
        in={fixtureOverlayOpen}
        direction="down"
        timeout={{ enter: 350, exit: 300 }}
        unmountOnExit
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 11,
            pointerEvents: "auto",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {fixtureOverlayContent}
        </Box>
      </Slide>
    </>
  );

  // Copilot right column: 50% action buttons (tabs) on top, 50% stats below.
  // Each half scrolls internally if its content overflows.
  const rightColumnElement = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2, minHeight: 0 }}>
      {/* With stats, the buttons take exactly three rows (see the grid maxHeight) and
          the stats get the rest; without stats the buttons fill the column. */}
      <Box sx={{ flex: copilotTabControl.showStats ? "0 0 auto" : 1, minHeight: 0, display: "flex", transform: "translateY(-2px)" }}>{controlsElement}</Box>
      {/* Stats only on IN-MATCH; elsewhere the buttons get the space. */}
      {copilotTabControl.showStats && (
        <>
          <Box sx={{ height: "1px", flexShrink: 0, bgcolor: "rgba(255,255,255,0.12)" }} />
          <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>{statsElement}</Box>
        </>
      )}
    </Box>
  );

  // Change-renderer overlay (top drawer) — rendered in the copilot grid, which
  // has no controls panel of its own.
  const rendererOverlayEl = (
    <>
      <Fade in={rendererOverlayOpen} timeout={{ enter: 300, exit: 250 }} unmountOnExit>
        <Box
          onClick={onCloseRendererOverlay}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "40%",
            bgcolor: "rgba(0,0,0,0.85)",
            zIndex: 12,
            borderRadius: 2,
          }}
        />
      </Fade>
      <Slide in={rendererOverlayOpen} direction="down" timeout={{ enter: 350, exit: 300 }} unmountOnExit>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "40%",
            zIndex: 13,
            pointerEvents: "auto",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {rendererOverlayContent}
        </Box>
      </Slide>
    </>
  );
 return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {isCopilot ? (
        <Box
          ref={measureGridWidth}
          sx={{
            position: "relative",
            // Kill react-grid-layout's mount transition so tiles don't slide in
            // left-to-right on load. (Drag still snaps; it's just the CSS tween.)
            "& .react-grid-item": { transition: "none !important" },
            // The team sheet (@ae/lineup) sizes to its rows; stretch it to its
            // grid item and let the bench card take the rest, so both sheets
            // end on the same line as the stats.
            "& :is([data-player-list='home'], [data-player-list='away']) > div": { height: "100%" },
            "& :is([data-player-list='home'], [data-player-list='away']) > div > div:last-of-type": { flex: 1 },
            // The bench card's two inner surfaces fill it too.
            "& :is([data-player-list='home'], [data-player-list='away']) > div > div:last-of-type > div, & :is([data-player-list='home'], [data-player-list='away']) > div > div:last-of-type > div > div": { height: "100%" },
            // Compact the copilot lineup ROWS on shorter viewports so a full
            // 9-sub bench fits at 1920×950 (1:1 at 1080). @ae/lineup has no
            // density prop, so we override its data-player-row cells app-side.
            "& [data-player-row='true']": { py: `${(2.5 * copilotScale).toFixed(1)}px` },
            // Inner flex (badge + name): tighten the badge↔name gap.
            "& [data-player-row='true'] > div": { gap: `${Math.round(8 * copilotScale)}px` },
            // Player name (Typography body2): the surname carries the row,
            // the given names sit back at 500 and 72% white.
            "& [data-player-row='true'] .MuiTypography-body2": {
              fontSize: `${Math.round(21 * copilotScale)}px`,
              fontWeight: 500,
              color: "rgba(255,255,255,0.72)",
            },
            "& [data-player-row='true'] .MuiTypography-body2 > span": {
              fontWeight: 700,
              color: "#fff",
            },
            // Number badge (first child): lib default 28px → 24px "sub icon".
            "& [data-player-row='true'] > div > :first-of-type": {
              width: `${Math.round(31 * copilotScale)}px !important`,
              height: `${Math.round(31 * copilotScale)}px !important`,
              fontSize: `${Math.round(18 * copilotScale)}px !important`,
            },
          }}
        >
          {sharedOverlays}
          {rendererOverlayEl}
          {gridWidth > 0 && (
            <Responsive
              className="layout"
              // Width is measured by us (measureGridWidth) before paint, so the
              // grid renders at its final layout on the first frame — no slide.
              width={gridWidth}
              layouts={{ lg: copilotLayout }}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 24, md: 24, sm: 12, xs: 8, xxs: 4 }}
              rowHeight={isCopilot ? copilotRowHeight : 100}
              margin={[COPILOT_GAP, COPILOT_GAP]}
              containerPadding={[COPILOT_EDGE, COPILOT_EDGE]}
              compactType={null}
              isDraggable={false}
              isResizable={false}
            >
              <div key="clock" style={{ height: '100%' }}>{clockElement}</div>
              <div key="homeLineup" style={{ height: '100%' }}>{homeLineupElement}</div>
              <div key="awayLineup" style={{ height: '100%' }}>{awayLineupElement}</div>
              <div key="rightCol" style={{ height: '100%' }}>{rightColumnElement}</div>
            </Responsive>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 2, p: 2 }}>
          {/* Left Panel: Lineups & Stats */}
          {/* align-items:start so each column sizes to its OWN content. Default
              grid stretch inflated every column to the tallest one (a 16-sub
              lineup), so the short stats column ballooned into a big dark blank
              card below its rows ("adjusts height based on other stuff"). */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, flex: 1, position: "relative", alignItems: "start" }}>
            {sharedOverlays}
            {homeLineupElement}
            {fullStatsOriginalElement}
            {awayLineupElement}
          </Box>
          {/* Right Panel: Controls & Preview */}
          {controlsElement}
        </Box>
      )}

      {/* Maestro modal is now rendered inside DriveMaestro */}

      {/* Headless stats animator — copilot stat tiles animate directly to the
          renderer (no modal) via a hidden MaestroProvider. */}
      {isCopilot && hasFixtureSelected && statGraphicDef?.path && (
        <HeadlessStatsAnimator
          graphicId={statGraphicDef.id || STAT_GRAPHIC}
          graphicPath={statGraphicDef.path}
          graphicName={statGraphicDef.name || STAT_GRAPHIC}
          brokerService={brokerServiceObj}
          dataService={dataService}
          bindData={bindData}
          rendererId={selectedRenderer?.renderer_id || rendererId}
          target={statAnimateTarget}
          initialFormData={statAnimateTarget.formData}
          onFired={(caption, mode, err) =>
            showNotification(
              err
                ? `Failed to animate ${caption}`
                : mode === "OUT"
                  ? `Animated out ${caption}`
                  : `Animating ${caption}`,
              err ? "error" : "success",
            )
          }
        />
      )}

      {/* Dialog cluster (P10). All 4 operator-triggered modals are lazy
          and gated by this <Suspense>. Fallback is null — a flash of
          empty space is fine because the user just clicked to open one. */}
      <Suspense fallback={null}>

      <AddGoalDetailsDialog
        open={copilotGoalDialogOpen}
        onClose={handleGoalDialogClose}
        onConfirmed={() => { goalConfirmedRef.current = true; }}
        teamName={copilotGoalTeam === 'home' ? homeTeam?.team_name : awayTeam?.team_name}
        teamSide={copilotGoalTeam}
        teamColor={copilotGoalTeam === 'home' ? homeAccentColor : awayAccentColor}
        teamLogo={copilotGoalTeam === 'home' ? homeTeamLogo : awayTeamLogo}
        oppositeTeamColor={copilotGoalTeam === 'home' ? awayAccentColor : homeAccentColor}
        players={copilotGoalTeam === 'home' ? [...homeLineup.players, ...homeLineup.subs] : [...awayLineup.players, ...awayLineup.subs]}
        oppositePlayers={copilotGoalTeam === 'home' ? [...awayLineup.players, ...awayLineup.subs] : [...homeLineup.players, ...homeLineup.subs]}
        clockTime={clockTime}
        clockSeconds={goalClickSeconds}
        oldScore={goalPreScore}
        fixture={fixture}
        result={result}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        fixtureId={selectedFixtureId}
        competitionId={competitionId}
        seasonId={seasonId}
        profileId={profileId}
        userRole={userRole}
        eventService={eventService}
        triggerScoreUpdate={triggerScoreUpdate}
        updateFixtureState={updateFixtureState}
        onNotify={showNotification}
      />

      <DuplicateInfoDialog
        open={fixtureDuplicatesOpen}
        duplicates={fixtureDuplicates}
        summary
        onCancel={() => setFixtureDuplicatesOpen(false)}
        onConfirm={() => setFixtureDuplicatesOpen(false)}
      />


      <CopilotSubstitutionDialog
        open={substitutionsDialogOpen}
        onClose={() => setSubstitutionsDialogOpen(false)}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        homeLineup={homeLineup}
        awayLineup={awayLineup}
        homeColor={homeAccentColor}
        awayColor={awayAccentColor}
        rendererUrl={selectedRenderer?.renderer_id ? `${process.env.REACT_APP_RENDERER_PREVIEW_URL}/?renderer=${encodeURIComponent(selectedRenderer.renderer_id)}` : `${process.env.REACT_APP_RENDERER_PREVIEW_URL}/?renderer=OP-BLUEBIRD-TEST929`}
        fixtureId={selectedFixtureId}
        competitionId={competitionId}
        seasonId={seasonId}
        clockTime={clockTime}
        gamePeriod={fixture?.fixture_session ?? null}
        homeSubsMade={homeSubsMade}
        awaySubsMade={awaySubsMade}
        homeConcussionSubs={homeConcussionSubs}
        awayConcussionSubs={awayConcussionSubs}
        homeIneligiblePlayerIds={homeIneligiblePlayerIds}
        awayIneligiblePlayerIds={awayIneligiblePlayerIds}
        updateRenderer={updateRenderer}
        getRendererIdToUse={getRendererIdToUse}
        eventService={eventService}
        profileId={profileId}
        userRole={userRole}
        onAnimateInMaestro={handleAnimateSubstitution}
      />
      </Suspense>

      {isCopilot && (
        <>
          <ClockTimeInputDialog
            open={copilotClockTimeOpen}
            onClose={() => setCopilotClockTimeOpen(false)}
            onConfirm={handleCopilotClockTimeConfirm}
            minMinutes={copilotClockConstraints.min}
            maxMinutes={copilotClockConstraints.max}
          />
          <ResetClockConfirmDialog
            open={copilotResetConfirmOpen}
            onClose={() => setCopilotResetConfirmOpen(false)}
            onConfirm={doCopilotClockReset}
          />
          <OverrideClockConfirmDialog
            open={!!clockOverride}
            periodLabel={clockOverride?.periodLabel}
            onClose={() => setClockOverride(null)}
            onConfirm={doClockOverride}
          />
        </>
      )}

      <ShortcutRegistry
        open={shortcutRegistryOpen}
        onClose={() => setShortcutRegistryOpen(false)}
        items={shortcutRegistryItems}
      />

      {/* Mounted only while open, so the lazy chunk is never fetched for an
          operator who never opens it — and `fixtureState` here is the merged
          object, i.e. it includes season.all_fixtures, which is the whole point
          of being able to compare the two nodes side by side. */}
      {ddsViewerOpen && (
        <Suspense fallback={null}>
          <DdsObjectViewer
            open={ddsViewerOpen}
            onClose={onCloseDdsViewer}
            state={fixtureState}
          />
        </Suspense>
      )}

    </Box>
  );
};

export default TXLive;
